import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { KCard } from "@/components/KCard";
import { Button } from "@/components/ui/button";
import { ExamResultReport } from "@/components/ExamResultReport";

import { ExamSecurityWrapper } from "@/components/ExamSecurityWrapper";
import { AudioPlayerBlock } from "@/components/StrictAudioPlayer";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
    GraduationCap,
    Clock,
    Award,
    Loader2,
    PlusCircle,
    Volume2,
    VolumeX,
    BookOpen,
    Edit3,
    Layers,
    CheckCircle2,
    ChevronRight,
    ChevronUp,
    ChevronDown,
    ChevronLeft,
    Flag,
    HelpCircle,
    LogOut,
    PanelRightClose,
    PanelRightOpen,
    Check,
    Play,
    AlertCircle,
    X
} from "lucide-react";
import { cn } from "@/lib/utils";
import { shuffleScenarioQuestionGroups } from "@/lib/shuffleSeeded";
import {
    buildMcqDisplayPairs,
    migrateLoadedMcqToIndex,
    normalizeMcqAnswersForApi,
    isQuestionAnswered,
    getMcqOptionTexts,
} from "@/lib/mcqAnswer";
import { mcqDisplayBadge } from "@/lib/mcqOptionIds";
import { NoCopyQuestionStem } from "@/components/NoCopyQuestionStem";
import { CustomSecureAudioPlayer } from "@/components/CustomSecureAudioPlayer";
import { useAuth } from "@/auth/AuthContext";
import { ScenarioBlock } from "@/components/exam/ScenarioBlock";
import {
    getAssignedMockTests,
    getMockTestQuestions,
    getMockTests,
    startMockTest,
    getMockTestAttempt,
    saveMockTestProgress,
    submitMockTest,
    deleteMockTest,
    duplicateMockTest,
    getMockTestAccessStatus,
    purchaseMockTest,
    updateMockTest,
    getMockTestAttemptReview,
    getSignedAudioUrl,
    getMockTestSubscriptionPlans,
    getMyMockTestSubscription,
    type MockTest,
    type MockTestQuestion,
    type MockTestAttempt,
    type MockTestAccessStatus,
    type MockTestSubscriptionPlan,
    type MockTestSubscription,
} from "@/api/lms/mock-tests";
import { getMockTestHistory } from "@/api/lms/examHistory";
import { initMockTestBkashPayment, initSubscriptionBkashPayment } from "@/api/lms/bkash";
import { usePaymentRedirectResult } from "@/hooks/usePaymentRedirectResult";
import { PolicyAcceptance } from "@/components/PolicyAcceptance";
import { CouponInput } from "@/components/CouponInput";
import type { CouponDiscountResult } from "@/api/lms/coupons";
import { useQuery } from "@tanstack/react-query";
import { fetchProfile } from "@/api/userAccount";
import { formatMockTestLevelsForDisplay, parseMockTestLevelField } from "@/lib/mockTestLevels";
import { renderFurigana } from "@/lib/japaneseHelpers";
import { ExamReviewPanel } from "@/components/ExamReviewPanel";
import type { ReviewableQuestion } from "@/components/ExamReviewPanel";

import { examConfigApi } from "@/api/admin/examConfig";
type MockSection = string;

// Section-level timing (JLPT/JFT official structure)
type SectionTimings = {
    VOCABULARY: number;
    GRAMMAR: number;
    READING: number;
    LISTENING: number;
};

const DEFAULT_SECTION_TIMINGS: SectionTimings = {
    VOCABULARY: 25,
    GRAMMAR: 20,
    READING: 30,
    LISTENING: 25,
};

const SECTION_TIMING_STORAGE_KEY = (mockTestId: string) =>
    `mock_timings_${mockTestId}`;

const SECTION_START_STORAGE_KEY = (attemptId: string, section: string) =>
    `mock_section_start_${attemptId}_${section}`;

// ── Audio Token Cache ─────────────────────────────────────────────────────────
// Caches signed S3 audio URLs for 4 minutes to avoid repeated backend calls.
// Key: `${attemptId}:${rawAudioUrl}`, Value: { signedUrl, expiresAt }
const audioTokenCache = new Map<string, { signedUrl: string; expiresAt: number }>();

/**
 * Extracts the S3 object key from a standard S3 public URL.
 * e.g. https://bucket.s3.region.amazonaws.com/uploads/lms/audios/xxx.mp3
 *   -> uploads/lms/audios/xxx.mp3
 * Returns null if the URL is not an S3 URL or is already a signed URL.
 */
function extractS3KeyFromUrl(url: string): string | null {
    if (!url) return null;
    // Already a signed URL (has X-Amz-Signature) — don't double-sign
    if (url.includes('X-Amz-Signature')) return null;

    if (!url.startsWith('http://') && !url.startsWith('https://')) {
        return decodeURIComponent(url).replace(/^\//, '');
    }

    try {
        const parsed = new URL(url);
        const key = decodeURIComponent(parsed.pathname).replace(/^\//, '');
        return key || null;
    } catch {
        return null;
    }
}

function getPlayLimitFromUrl(url: string | null, fallback: number): number {
    if (!url) return fallback;
    try {
        const match = url.match(/[?&]playLimit=([^&]+)/);
        if (match) {
            if (match[1] === "unlimited") return 0;
            const parsed = parseInt(match[1], 10);
            if (!isNaN(parsed)) return parsed;
        }
    } catch { }
    return fallback;
}

/**
 * Resolves a raw S3 audio URL to a short-lived signed URL via the backend.
 * Falls back to the raw URL if resolution fails or no attemptId is available.
 * Results are cached for 4 minutes.
 */
async function resolveSignedAudioUrl(rawUrl: string, attemptId: string): Promise<string> {
    if (!rawUrl || !attemptId) return rawUrl;
    const cacheKey = `${attemptId}:${rawUrl}`;
    const cached = audioTokenCache.get(cacheKey);
    if (cached && Date.now() < cached.expiresAt) {
        return cached.signedUrl;
    }
    const s3Key = extractS3KeyFromUrl(rawUrl);
    if (!s3Key) return rawUrl; // Can't extract key — return as-is
    try {
        const result = await getSignedAudioUrl(attemptId, s3Key);
        const CACHE_DURATION_MS = 4 * 60 * 1000; // 4 minutes (token lasts 5)
        audioTokenCache.set(cacheKey, {
            signedUrl: result.signedUrl,
            expiresAt: Date.now() + CACHE_DURATION_MS,
        });
        return result.signedUrl;
    } catch (err) {
        console.warn('[AudioToken] Failed to resolve signed URL, using raw URL as fallback:', err);
        return rawUrl;
    }
}

function getQuestionCategory(q: MockTestQuestion, examConfig?: any): string {
    const meta = (q.options as { metadata?: any })?.metadata || {};
    // Return the stored category. If empty, fall back to VOCABULARY only as last resort.
    let cat = meta.examCategory;
    if (cat && typeof cat === "string" && cat.trim().length > 0) {
        cat = cat.trim();
    } else {
        cat = "VOCABULARY";
    }

    // Attempt to map legacy JLPT categories to the current exam config's categories if no exact match exists
    if (examConfig && examConfig.sections) {
        if (examConfig.sections.some((s: any) => s.code === cat)) {
            return cat;
        }

        if (cat === "VOCABULARY") {
            const sv = examConfig.sections.find((s: any) => s.code === "SCRIPT_VOCABULARY");
            if (sv) return sv.code;
        } else if (cat === "GRAMMAR") {
            const ce = examConfig.sections.find((s: any) => s.code === "CONVERSATION_EXPRESSION");
            if (ce) return ce.code;
        } else if (cat === "READING") {
            const rc = examConfig.sections.find((s: any) => s.code === "READING_COMPREHENSION");
            if (rc) return rc.code;
        } else if (cat === "LISTENING") {
            const lc = examConfig.sections.find((s: any) => s.code === "LISTENING_COMPREHENSION");
            if (lc) return lc.code;
        }
    }

    return cat;
}

function groupMockQuestions(questions: MockTestQuestion[]) {
    const keyOrder: string[] = [];
    const byKey = new Map<string, MockTestQuestion[]>();
    for (const q of questions) {
        const key = q.scenarioId ?? "__none__";
        if (!byKey.has(key)) {
            byKey.set(key, []);
            keyOrder.push(key);
        }
        byKey.get(key)!.push(q);
    }
    return keyOrder.map((key) => {
        const qs = byKey.get(key)!;
        const scenario = key === "__none__" ? null : qs[0]?.scenario ?? null;
        return { scenario, questions: qs };
    });
}

function canEditMockTest(test: MockTest, isAdmin: boolean) {
    return isAdmin || Boolean(test.canManageQuestions);
}

const CIRCLE_BADGES = ["①", "②", "③", "④", "⑤", "⑥", "⑦", "⑧", "⑨", "⑩"];
function getCircleBadge(index: number) {
    return CIRCLE_BADGES[index] || String(index + 1);
}

const isHorizontalLayout = (optionsPairs: { text: string; imageUrl?: string | null; audioUrl?: string | null }[]) => {
    if (optionsPairs.length !== 4) return false;
    const hasMedia = optionsPairs.some(opt => opt.imageUrl || opt.audioUrl);
    if (hasMedia) return false;
    return optionsPairs.every(opt => opt.text.trim().length <= 15);
};

export default function MockTestPage() {
    const { user } = useAuth();
    const navigate = useNavigate();

    const profileQuery = useQuery({
        queryKey: ["profile"],
        queryFn: fetchProfile,
        enabled: Boolean(user),
    });

    const profileName = useMemo(() => {
        const p = profileQuery.data?.profile;
        if (p && (p.firstName || p.lastName)) {
            return [p.firstName, p.lastName].filter(Boolean).join(" ");
        }
        return null;
    }, [profileQuery.data]);
    const [searchParams, setSearchParams] = useSearchParams();
    const queryTestId = searchParams.get("testId");
    const queryExamType = searchParams.get("examType");
    const queryLevel = searchParams.get("level");

    const [allMockTests, setAllMockTests] = useState<MockTest[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedExamType, setSelectedExamType] = useState<string>("ALL");
    const [selectedLevel, setSelectedLevel] = useState(() => {
        return queryLevel || "ALL";
    });

    useEffect(() => {
        setSelectedLevel("ALL");
    }, [selectedExamType]);

    const { data: activeConfigs = [] } = useQuery({
        queryKey: ["activeExamConfigs"],
        queryFn: () => examConfigApi.getActive(),
    });

    const examTypeOptions = useMemo(() => {
        return ["ALL", ...activeConfigs.map(c => c.code)];
    }, [activeConfigs]);

    const levels = useMemo(() => {
        if (selectedExamType === "ALL") {
            const allLevels = activeConfigs.flatMap(c => c.levels?.map(l => l.code) || []);
            // Sort by sortOrder across all? Or just leave it as they come from API
            // Actually they are sorted per config from API. Let's dedupe them.
            return ["ALL", ...Array.from(new Set(allLevels))];
        }
        const config = activeConfigs.find(c => c.code === selectedExamType);
        if (!config) return ["ALL"];
        const sorted = (config.levels || []).sort((a, b) => a.sortOrder - b.sortOrder);
        return ["ALL", ...sorted.map(l => l.code)];
    }, [selectedExamType, activeConfigs]);
    const [sortBy, setSortBy] = useState<"newest" | "popular" | "price">("newest");
    const [selectedMockTestId, setSelectedMockTestId] = useState<string>("");
    const [selectedQuestions, setSelectedQuestions] = useState<MockTestQuestion[]>([]);
    const [loadingQuestions, setLoadingQuestions] = useState(false);
    const [myBestScores, setMyBestScores] = useState<Record<string, number>>({});
    const [purchasingId, setPurchasingId] = useState<string | null>(null);
    const [appliedMockCoupon, setAppliedMockCoupon] = useState<CouponDiscountResult | null>(null);
    // Student must accept the policies before purchasing a mock test.
    const [mockPolicyAccepted, setMockPolicyAccepted] = useState(false);
    // How many attempts the student wants to buy (per-attempt pricing).
    const [purchaseQuantity, setPurchaseQuantity] = useState(1);
    // Subscribing to an all-access plan.
    const [subscribingPlan, setSubscribingPlan] = useState<string | null>(null);

    // Purchase access status for selected test (student only)
    const { data: accessStatus, refetch: refetchAccess } = useQuery<MockTestAccessStatus>({
        queryKey: ["mockTestAccess", selectedMockTestId],
        queryFn: () => getMockTestAccessStatus(selectedMockTestId),
        enabled: !!selectedMockTestId && user?.role === "student",
        staleTime: 30_000,
    });

    // All-access subscription: plans catalogue + the student's current status.
    const { data: subscriptionPlans } = useQuery<MockTestSubscriptionPlan[]>({
        queryKey: ["mockTestSubscriptionPlans"],
        queryFn: getMockTestSubscriptionPlans,
        enabled: user?.role === "student",
        staleTime: 5 * 60_000,
    });
    const { data: mySubscription, refetch: refetchSubscription } = useQuery<{
        active: MockTestSubscription | null;
        history: MockTestSubscription[];
    }>({
        queryKey: ["myMockTestSubscription"],
        queryFn: getMyMockTestSubscription,
        enabled: user?.role === "student",
        staleTime: 30_000,
    });

    // Reset the quantity selector whenever the selected test changes.
    useEffect(() => {
        setPurchaseQuantity(1);
    }, [selectedMockTestId]);

    // Max attempts a student may buy in one purchase. Kept generous and
    // independent of attemptLimit (which is just the default grant), so buyers
    // can always increase the quantity. Respects a higher admin attemptLimit.
    const attemptCap = Math.max(10, accessStatus?.mockTest?.attemptLimit ?? 1);

    // Subscribe to an all-access plan via bKash. Free plans activate instantly;
    // paid plans redirect to the bKash checkout and activate on return.
    const handleSubscribe = async (plan: MockTestSubscriptionPlan) => {
        try {
            setSubscribingPlan(plan.id);
            const res = await initSubscriptionBkashPayment(plan.id);
            if (res.bkashURL) {
                toast.success("Redirecting to bKash payment...");
                window.location.href = res.bkashURL;
                return;
            }
            await Promise.all([refetchSubscription(), refetchAccess()]);
            toast.success("All-access subscription active! Every mock test is unlocked.");
        } catch (e: any) {
            toast.error(e?.message ?? "Subscription failed");
        } finally {
            setSubscribingPlan(null);
        }
    };

    // Report the outcome of a bKash checkout when the gateway redirects back here,
    // and refetch access so the purchased test unlocks without a manual reload.
    usePaymentRedirectResult(() => {
        void refetchAccess();
    });


    // Active attempt states
    const [mockAttemptId, setMockAttemptId] = useState<string | null>(null);
    const [mockAttempt, setMockAttempt] = useState<MockTestAttempt | null>(null);
    const [mockAnswers, setMockAnswers] = useState<Record<string, unknown>>({});
    const [mockResult, setMockResult] = useState<MockTestAttempt | null>(null);
    const [mockNowTick, setMockNowTick] = useState(() => Date.now());
    const [showAnswersReview, setShowAnswersReview] = useState(false);
    const [jftListeningConfirmed, setJftListeningConfirmed] = useState(false);

    const clockSkewRef = useRef(0);
    const [isSyncing, setIsSyncing] = useState(false);
    const [isOffline, setIsOffline] = useState(!navigator.onLine);

    const reviewQuery = useQuery({
        queryKey: ["mockTestReview", selectedMockTestId, mockResult?.id],
        queryFn: async () => {
            if (!selectedMockTestId || !mockResult?.id) return [];
            return getMockTestAttemptReview(selectedMockTestId, mockResult.id);
        },
        enabled: !!selectedMockTestId && !!mockResult?.id,
    });

    const selectedMockTest = useMemo(
        () => allMockTests.find((t) => t.id === selectedMockTestId) ?? null,
        [allMockTests, selectedMockTestId]
    );

    // Price per attempt for the selected test (per-attempt pricing).
    const unitPrice = Number(selectedMockTest?.price ?? 0);

    const currentExamConfig = useMemo(() => {
        if (!selectedMockTest?.examType) return null;
        return activeConfigs.find(c => c.code === selectedMockTest.examType) || null;
    }, [activeConfigs, selectedMockTest]);

    const getSynchronizedNow = useCallback(() => {
        return Date.now() + clockSkewRef.current;
    }, []);

    const getSectionDisplay = useCallback((secCode: string, isJapanese: boolean = false) => {
        const secConfig = currentExamConfig?.sections?.find(s => s.code === secCode);
        if (secConfig) {
            return isJapanese ? (secConfig.japaneseName || secConfig.name) : (secConfig.englishName || secConfig.name);
        }
        if (secCode === "VOCABULARY") return isJapanese ? "文字・語彙" : "Vocabulary";
        if (secCode === "GRAMMAR") return isJapanese ? "文法" : "Grammar";
        if (secCode === "READING") return isJapanese ? "読解" : "Reading";
        if (secCode === "LISTENING") return isJapanese ? "聴解" : "Listening";
        return secCode;
    }, [currentExamConfig]);

    const sectionBreakdown = useMemo(() => {
        // `incorrect` and `skipped` are part of this shape because the result card
        // renders a correct/incorrect/unanswered breakdown from it. They were read
        // downstream but never declared or counted here, so both always came back
        // undefined and the breakdown grid rendered blank.
        type SectionStats = {
            correct: number;
            incorrect: number;
            skipped: number;
            total: number;
            score: number;
            maxScore: number;
        };
        const emptyStats = (): SectionStats => ({
            correct: 0,
            incorrect: 0,
            skipped: 0,
            total: 0,
            score: 0,
            maxScore: 0,
        });

        const stats: Record<string, SectionStats> = {};
        // Find which base set of sections we should display
        let allSecs = currentExamConfig?.sections?.map(s => s.code) || ["VOCABULARY", "GRAMMAR", "READING", "LISTENING"];

        // If none of the config sections match any question, we are probably dealing with legacy mock test questions
        if (!allSecs.some(sec => selectedQuestions.some(q => getQuestionCategory(q, currentExamConfig) === sec))) {
            allSecs = ["VOCABULARY", "GRAMMAR", "READING", "LISTENING"];
        }

        allSecs.forEach(sec => {
            stats[sec] = emptyStats();
        });
        if (!selectedQuestions.length) return stats;

        // Ensure we grade the normalized string answers (matching the backend's logic)
        const normalizedAnswers = mockResult?.answers
            ? (mockResult.answers as Record<string, unknown>)
            : normalizeMcqAnswersForApi(selectedQuestions, mockAnswers);

        const questionsToGrade = reviewQuery.data?.length ? reviewQuery.data : selectedQuestions;

        questionsToGrade.forEach((q) => {
            const section = getQuestionCategory(q, currentExamConfig);
            if (!stats[section]) {
                stats[section] = emptyStats();
            }
            const marks = q.marks ?? 1;
            stats[section].total += 1;
            stats[section].maxScore += marks;

            const userAnswer = normalizedAnswers[q.id];
            const userAnsStr = String(userAnswer ?? "").trim();
            const correctAnsStr = String(q.correctAnswer ?? "").trim();

            // Unanswered questions are their own bucket — counting them as
            // incorrect would overstate wrong answers in the breakdown.
            const isSkipped =
                userAnswer === undefined || userAnswer === null || userAnsStr === "";
            if (isSkipped) {
                stats[section].skipped += 1;
                return;
            }

            let isCorrect = false;

            if (userAnswer !== undefined && userAnswer !== null && userAnsStr === correctAnsStr) {
                isCorrect = true;
            } else {
                // Fallback for option ID matching
                let matchedByOption = false;
                if (q.options && typeof q.options === "object") {
                    const optionsData = q.options as any;
                    const optsArray = optionsData.options || optionsData;
                    if (Array.isArray(optsArray)) {
                        const correctOption = optsArray.find((o: any) => o.id === correctAnsStr);
                        if (correctOption) {
                            const matchVal = String(correctOption.text || correctOption.imageUrl || "").trim();
                            if (matchVal === userAnsStr) {
                                matchedByOption = true;
                            }
                        }
                    }
                }
                if (matchedByOption) isCorrect = true;
            }

            if (isCorrect) {
                stats[section].correct += 1;
                stats[section].score += marks;
            } else {
                stats[section].incorrect += 1;
            }
        });

        return stats;
        // currentExamConfig drives both section bucketing and question categorisation,
        // so a config arriving after first render must recompute the breakdown.
    }, [selectedQuestions, mockAnswers, mockResult, reviewQuery.data, currentExamConfig]);

    // CBT visualizer and panel navigation states
    const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
    const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);
    const [isSectionGateOpen, setIsSectionGateOpen] = useState(true);
    const [flaggedQuestionIds, setFlaggedQuestionIds] = useState<string[]>([]);
    const [playedAudioIds, setPlayedAudioIds] = useState<string[]>([]);
    const [isNavigatorOpen, setIsNavigatorOpen] = useState(true);
    const [confirmModalType, setConfirmModalType] = useState<"section" | "submit" | "exit" | null>(null);

    // Audio simulation states
    const [audioPlaying, setAudioPlaying] = useState(false);
    const [audioDuration, setAudioDuration] = useState(0);
    const [audioCurrentTime, setAudioCurrentTime] = useState(0);
    const [playingAudioId, setPlayingAudioId] = useState<string | null>(null);
    const [bypassedAudioIds, setBypassedAudioIds] = useState<string[]>([]);
    const [highlightedQuestionId, setHighlightedQuestionId] = useState<string | null>(null);

    const markAudioAsPlayedForId = useCallback((id: string) => {
        const targetQ = selectedQuestions.find(q => q.id === id);
        let qIdsToMark: string[] = [];
        if (targetQ) {
            if (targetQ.scenarioId) {
                qIdsToMark = selectedQuestions.filter(q => q.scenarioId === targetQ.scenarioId).map(q => q.id);
            } else {
                qIdsToMark = [targetQ.id];
            }
        } else { // Scenario ID
            qIdsToMark = selectedQuestions.filter(q => q.scenarioId === id).map(q => q.id);
        }

        const nextPlayed = [...new Set([...playedAudioIds, ...qIdsToMark, id])];
        setPlayedAudioIds(nextPlayed);
        if (mockAttemptId) {
            localStorage.setItem(`mock_attempt_${mockAttemptId}_played_audio`, JSON.stringify(nextPlayed));
        }
    }, [selectedQuestions, playedAudioIds, mockAttemptId]);

    const stopAllAudio = useCallback(() => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        }
        setAudioPlaying(false);
        setPlayingAudioId(null);
    }, []);

    useEffect(() => {
        stopAllAudio();
        return () => stopAllAudio();
    }, [currentSectionIndex, mockResult, stopAllAudio]);

    const playAudio = useCallback(async (id: string, rawUrl: string) => {
        if (playingAudioId === id) return;

        if (audioRef.current && mockAttemptId) {
            audioRef.current.pause();
            setPlayingAudioId(id);
            setAudioPlaying(false);
            setAudioDuration(0);
            setAudioCurrentTime(0);

            try {
                const signedUrl = await resolveSignedAudioUrl(rawUrl, mockAttemptId);
                audioRef.current.src = signedUrl;
                audioRef.current.load();
                audioRef.current.play().catch(() => { });
            } catch (err) {
                console.error("Failed to load secure audio URL", err);
                toast.error("Could not load audio. Please check your connection.");
                setPlayingAudioId(null);
            }
        }
    }, [playingAudioId, mockAttemptId, resolveSignedAudioUrl]);

    const bypassAudio = useCallback((id: string) => {
        const targetQ = selectedQuestions.find(q => q.id === id);
        let qIdsToMark: string[] = [];
        if (targetQ) {
            if (targetQ.scenarioId) {
                qIdsToMark = selectedQuestions.filter(q => q.scenarioId === targetQ.scenarioId).map(q => q.id);
            } else {
                qIdsToMark = [targetQ.id];
            }
        } else {
            qIdsToMark = selectedQuestions.filter(q => q.scenarioId === id).map(q => q.id);
        }

        const nextBypassed = [...new Set([...bypassedAudioIds, ...qIdsToMark, id])];
        setBypassedAudioIds(nextBypassed);
    }, [selectedQuestions, bypassedAudioIds]);

    const mockSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const sectionTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Per-section timer state
    const [sectionTimings, setSectionTimings] = useState<SectionTimings>({ ...DEFAULT_SECTION_TIMINGS });
    const [sectionSecondsLeft, setSectionSecondsLeft] = useState<number | null>(null);

    const isAdmin = user?.role === "admin";
    const isTeacher = user?.role === "teacher";
    const isStudent = user?.role === "student";
    const showAuthoringLinks = isAdmin || isTeacher;

    const filteredMockTests = useMemo(() => {
        let list = [...allMockTests];
        if (selectedExamType && selectedExamType !== "ALL") {
            list = list.filter(t => t.examType === selectedExamType);
        }
        if (selectedLevel && selectedLevel !== "ALL") {
            list = list.filter(t =>
                parseMockTestLevelField(t.level).some(l => l.toUpperCase() === selectedLevel.toUpperCase())
            );
        }
        if (sortBy === "price") {
            list.sort((a, b) => Number(a.price ?? 0) - Number(b.price ?? 0));
        } else if (sortBy === "popular") {
            list.sort((a, b) => (b.totalMarks ?? 0) - (a.totalMarks ?? 0));
        } else {
            list.sort((a, b) => (a.position ?? 9999) - (b.position ?? 9999));
        }
        return list;
    }, [allMockTests, selectedExamType, selectedLevel, sortBy]);

    /** Left list and right detail must stay in sync: avoid "no tests" + stale detail panel. */
    useEffect(() => {
        if (localStorage.getItem("active_mock_attempt_id")) return;
        if (filteredMockTests.length === 0) {
            if (selectedMockTestId) setSelectedMockTestId("");
            return;
        }
        if (!selectedMockTestId || !filteredMockTests.some((t) => t.id === selectedMockTestId)) {
            setSelectedMockTestId(filteredMockTests[0]!.id);
        }
    }, [filteredMockTests, selectedMockTestId]);

    // Sync deep link queries
    useEffect(() => {
        if (queryTestId && allMockTests.length > 0) {
            const found = allMockTests.find(t => t.id === queryTestId);
            if (found) {
                setSelectedMockTestId(queryTestId);
                const parsedLevels = parseMockTestLevelField(found.level);
                if (parsedLevels.length > 0) {
                    setSelectedLevel(parsedLevels[0]);
                }
            }
        }
    }, [queryTestId, allMockTests]);

    // Sync level search parameters
    useEffect(() => {
        const levelParam = searchParams.get("level");
        if (levelParam) {
            setSelectedLevel(levelParam);
        }
    }, [searchParams]);

    // Available exam sections that actually contain questions
    const availableSections = useMemo((): string[] => {
        const allSections = currentExamConfig?.sections?.map(s => s.code) || ["VOCABULARY", "GRAMMAR", "READING", "LISTENING"];
        let present = allSections.filter((sec) =>
            selectedQuestions.some((q) => getQuestionCategory(q, currentExamConfig) === sec)
        );

        if (present.length === 0) {
            const legacySections = ["VOCABULARY", "GRAMMAR", "READING", "LISTENING"];
            present = legacySections.filter((sec) =>
                selectedQuestions.some((q) => getQuestionCategory(q, currentExamConfig) === sec)
            );
        }

        return present.length > 0 ? present : ["VOCABULARY"];
    }, [selectedQuestions, currentExamConfig]);

    const activeSectionQuestions = useMemo(() => {
        const currentSec = availableSections[currentSectionIndex] || "VOCABULARY";
        return selectedQuestions.filter((q) => getQuestionCategory(q, currentExamConfig) === currentSec);
    }, [selectedQuestions, availableSections, currentSectionIndex]);

    const activeQuestion = useMemo((): MockTestQuestion | null => {
        return activeSectionQuestions[activeQuestionIndex] || null;
    }, [activeSectionQuestions, activeQuestionIndex]);

    const activeScenarioQuestions = useMemo(() => {
        if (!activeQuestion) return [];
        if (!activeQuestion.scenarioId) {
            return [activeQuestion];
        }
        return activeSectionQuestions.filter((q) => q.scenarioId === activeQuestion.scenarioId);
    }, [activeSectionQuestions, activeQuestion]);

    const activeSection = useMemo(() => {
        return availableSections[currentSectionIndex] || "VOCABULARY";
    }, [availableSections, currentSectionIndex]);

    const activeQuestionAudioUrl = useMemo(() => {
        return activeQuestion?.audioUrl || activeQuestion?.scenario?.audioUrl || null;
    }, [activeQuestion]);

    // Global counts for progress bar
    const totalMockQuestions = selectedQuestions.length;

    const answeredCountGlobal = useMemo(() => {
        return selectedQuestions.filter((q) => isQuestionAnswered(q, mockAnswers[q.id])).length;
    }, [selectedQuestions, mockAnswers]);

    const answeredCountSection = useMemo(() => {
        return activeSectionQuestions.filter((q) => isQuestionAnswered(q, mockAnswers[q.id])).length;
    }, [activeSectionQuestions, mockAnswers]);

    const progressPercentSection = useMemo(() => {
        const total = activeSectionQuestions.length;
        return total > 0 ? Math.round((answeredCountSection / total) * 100) : 0;
    }, [activeSectionQuestions, answeredCountSection]);

    const getAnswersPayload = useCallback((answersMap: Record<string, unknown>) => {
        const normalized = normalizeMcqAnswersForApi(selectedQuestions, answersMap);

        // Add metadata keys starting with __
        normalized.__current_section_index = currentSectionIndex;
        normalized.__active_question_index = activeQuestionIndex;
        normalized.__flagged_question_ids = flaggedQuestionIds;
        normalized.__played_audio_ids = playedAudioIds;
        normalized.__jft_listening_confirmed = jftListeningConfirmed;

        // Save current scroll position so we can restore it after reload
        const scrollEl = document.getElementById("questions-scroll-container");
        if (scrollEl) {
            normalized.__scroll_position = scrollEl.scrollTop;
        }

        const sectionStarts: Record<string, number> = {};
        if (mockAttemptId) {
            const keysToScan = [...availableSections, "JFT_GLOBAL"];
            for (const sec of keysToScan) {
                const key = SECTION_START_STORAGE_KEY(mockAttemptId, sec);
                const val = localStorage.getItem(key);
                if (val) {
                    sectionStarts[sec] = Number(val);
                }
            }
        }
        normalized.__section_starts = sectionStarts;

        return normalized;
    }, [
        selectedQuestions,
        currentSectionIndex,
        activeQuestionIndex,
        flaggedQuestionIds,
        playedAudioIds,
        jftListeningConfirmed,
        availableSections,
        mockAttemptId
    ]);

    // Online / Offline listener effect
    useEffect(() => {
        const handleOnline = () => {
            setIsOffline(false);
            toast.success("Connection restored. Syncing progress...");

            // Check if we have a pending offline submission
            const pendingSubmit = localStorage.getItem("pending_submit_attempt_id");
            if (pendingSubmit) {
                void (async () => {
                    try {
                        const savedAns = localStorage.getItem(`mock_attempt_${pendingSubmit}_answers`);
                        const ans = savedAns ? JSON.parse(savedAns) : {};
                        const answersPayload = normalizeMcqAnswersForApi(selectedQuestions, ans);
                        const submitted = await submitMockTest(pendingSubmit, { answers: answersPayload });
                        localStorage.removeItem("pending_submit_attempt_id");
                        localStorage.removeItem("active_mock_attempt_id");
                        setMockResult(submitted);
                        setMockAttemptId(null);
                        setMockAttempt(null);
                        toast.success("Exam submitted successfully!");
                    } catch (err) {
                        console.error("Pending submit failed:", err);
                    }
                })();
                return;
            }

            // Sync any unsaved progress
            if (mockAttemptId) {
                void (async () => {
                    try {
                        setIsSyncing(true);
                        const savedAns = localStorage.getItem(`mock_attempt_${mockAttemptId}_answers`);
                        const ans = savedAns ? JSON.parse(savedAns) : mockAnswers;
                        const payload = getAnswersPayload(ans);
                        const a = await saveMockTestProgress(mockAttemptId, { answers: payload });
                        if (a.serverTime) {
                            clockSkewRef.current = new Date(a.serverTime).getTime() - Date.now();
                        }
                        toast.success("Progress synchronized with server");
                    } catch (err) {
                        console.error("Sync failed:", err);
                    } finally {
                        setIsSyncing(false);
                    }
                })();
            }
        };

        const handleOffline = () => {
            setIsOffline(true);
            toast.warning("You are currently offline. Progress is being saved locally.", { duration: 5000 });
        };

        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);
        return () => {
            window.removeEventListener("online", handleOnline);
            window.removeEventListener("offline", handleOffline);
        };
    }, [mockAttemptId, getAnswersPayload, mockAnswers, selectedQuestions]);

    // Centralized debounced sync effect
    useEffect(() => {
        if (!mockAttemptId || mockResult) return;

        // Save to localStorage immediately as a fast local fallback
        localStorage.setItem(`mock_attempt_${mockAttemptId}_answers`, JSON.stringify(mockAnswers));
        localStorage.setItem(`mock_attempt_${mockAttemptId}_flags`, JSON.stringify(flaggedQuestionIds));
        localStorage.setItem(`mock_attempt_${mockAttemptId}_played_audio`, JSON.stringify(playedAudioIds));
        localStorage.setItem(`mock_attempt_${mockAttemptId}_section_idx`, String(currentSectionIndex));
        localStorage.setItem(`mock_attempt_${mockAttemptId}_question_idx`, String(activeQuestionIndex));
        localStorage.setItem(`active_mock_attempt_id`, mockAttemptId);
        // Persist scroll position for restore after reload
        const scrollEl = document.getElementById("questions-scroll-container");
        if (scrollEl) {
            localStorage.setItem(`mock_attempt_${mockAttemptId}_scroll`, String(scrollEl.scrollTop));
        }

        if (isOffline) return; // Don't sync to backend if we are offline

        setIsSyncing(true);
        const timer = setTimeout(async () => {
            try {
                const payload = getAnswersPayload(mockAnswers);
                const a = await saveMockTestProgress(mockAttemptId, { answers: payload });
                if (a.serverTime) {
                    clockSkewRef.current = new Date(a.serverTime).getTime() - Date.now();
                }
            } catch (err) {
                console.error("Autosave to backend failed:", err);
            } finally {
                setIsSyncing(false);
            }
        }, 1500);

        return () => {
            clearTimeout(timer);
        };
    }, [
        mockAttemptId,
        mockAnswers,
        flaggedQuestionIds,
        playedAudioIds,
        currentSectionIndex,
        activeQuestionIndex,
        mockResult,
        isOffline,
        getAnswersPayload
    ]);

    // Load section timings from mockTest metadata (fallback to localStorage if empty)
    useEffect(() => {
        if (!selectedMockTest) return;
        if (selectedMockTest.sectionDurations) {
            try {
                setSectionTimings({
                    ...DEFAULT_SECTION_TIMINGS,
                    ...JSON.parse(selectedMockTest.sectionDurations),
                });
            } catch {
                setSectionTimings({ ...DEFAULT_SECTION_TIMINGS });
            }
        } else {
            try {
                const stored = localStorage.getItem(SECTION_TIMING_STORAGE_KEY(selectedMockTest.id));
                if (stored) {
                    setSectionTimings({ ...DEFAULT_SECTION_TIMINGS, ...JSON.parse(stored) });
                } else {
                    setSectionTimings({ ...DEFAULT_SECTION_TIMINGS });
                }
            } catch {
                setSectionTimings({ ...DEFAULT_SECTION_TIMINGS });
            }
        }
    }, [selectedMockTest]);

    // Section-level countdown timer — starts/stops when gate opens/closes
    useEffect(() => {
        // Clear any running section timer
        if (sectionTimerRef.current) {
            clearInterval(sectionTimerRef.current);
            sectionTimerRef.current = null;
        }

        // If the section gate is open or we're not in an active attempt, pause timer
        if (isSectionGateOpen || !mockAttemptId || mockResult) {
            setSectionSecondsLeft(null);
            return;
        }

        const isJFT = selectedMockTest?.examType === "JFT_BASIC";
        const totalSecs = isJFT
            ? (selectedMockTest?.duration ?? 60) * 60
            : (sectionTimings[activeSection as keyof SectionTimings] ?? DEFAULT_SECTION_TIMINGS[activeSection as keyof SectionTimings]) * 60;

        // Check for a persisted start timestamp (for resumability)
        const startKey = isJFT
            ? SECTION_START_STORAGE_KEY(mockAttemptId, "JFT_GLOBAL")
            : SECTION_START_STORAGE_KEY(mockAttemptId, activeSection);
        let startTimestamp = Number(localStorage.getItem(startKey) || "0");

        const nowSync = getSynchronizedNow();
        // If no stored start or it's invalid, set now
        if (!startTimestamp || startTimestamp > nowSync) {
            startTimestamp = nowSync;
            localStorage.setItem(startKey, String(startTimestamp));

            // Save section start timestamp to DB immediately to block cheating/clearing localStorage
            void (async () => {
                try {
                    const payload = getAnswersPayload(mockAnswers);
                    if (!payload.__section_starts) payload.__section_starts = {};
                    if (isJFT) {
                        payload.__section_starts["JFT_GLOBAL"] = startTimestamp;
                    } else {
                        payload.__section_starts[activeSection] = startTimestamp;
                    }
                    await saveMockTestProgress(mockAttemptId, { answers: payload });
                } catch (err) {
                    console.error("Failed to save section start to backend:", err);
                }
            })();
        }

        const computeRemaining = () => {
            const elapsed = Math.floor((getSynchronizedNow() - startTimestamp) / 1000);
            return Math.max(0, totalSecs - elapsed);
        };

        setSectionSecondsLeft(computeRemaining());

        sectionTimerRef.current = setInterval(() => {
            const remaining = computeRemaining();
            setSectionSecondsLeft(remaining);

            if (remaining === 0) {
                if (sectionTimerRef.current) clearInterval(sectionTimerRef.current);
                // Auto-advance section or submit
                if (!isJFT && currentSectionIndex < availableSections.length - 1) {
                    toast.warning(`Time is up for ${getSectionDisplay(activeSection, true)}. Moving to next section.`, { duration: 4000 });
                    void (async () => {
                        const nextIdx = currentSectionIndex + 1;
                        setCurrentSectionIndex(nextIdx);
                        setActiveQuestionIndex(0);
                        setIsSectionGateOpen(selectedMockTest?.examType !== "JFT_BASIC");
                    })();
                } else {
                    toast.warning("Time has expired! Submitting your exam automatically...", { duration: 5000 });
                    void handleSubmitMockTest();
                }
            }
        }, 1000);

        return () => {
            if (sectionTimerRef.current) {
                clearInterval(sectionTimerRef.current);
                sectionTimerRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSectionGateOpen, mockAttemptId, activeSection, mockResult, selectedMockTest]);

    // Cleanup section timer on unmount
    useEffect(() => {
        return () => {
            if (sectionTimerRef.current) clearInterval(sectionTimerRef.current);
        };
    }, []);

    // Watch overall exam expiration (in case section timers don't catch it or for single-timer exams)
    useEffect(() => {
        if (!mockAttempt?.endsAt || mockResult || isOffline) return;

        const interval = setInterval(() => {
            const ends = new Date(mockAttempt.endsAt!).getTime();
            if (Date.now() >= ends) {
                clearInterval(interval);
                toast.warning("Total time has expired! Submitting your exam automatically...", { duration: 5000 });
                void handleSubmitMockTest();
            }
        }, 1000);

        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mockAttempt?.endsAt, mockResult, isOffline]);

    const sectionTimeLabel = useMemo(() => {
        if (sectionSecondsLeft === null) return null;
        const hrs = Math.floor(sectionSecondsLeft / 3600);
        const mins = Math.floor((sectionSecondsLeft % 3600) / 60);
        const secs = sectionSecondsLeft % 60;
        if (hrs > 0) {
            return `${hrs}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
        }
        return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    }, [sectionSecondsLeft]);



    // Reset audio playback when attempt changes
    useEffect(() => {
        setAudioPlaying(false);
        setAudioDuration(0);
        setAudioCurrentTime(0);
        setPlayingAudioId(null);
        setBypassedAudioIds([]);
        setHighlightedQuestionId(null);

        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.src = "";
        }
    }, [mockAttemptId]);

    // Scroll questions container to top when section changes or gate closes
    useEffect(() => {
        if (!isSectionGateOpen) {
            const container = document.getElementById("questions-scroll-container");
            if (container) {
                container.scrollTop = 0;
            }
        }
    }, [currentSectionIndex, isSectionGateOpen]);

    // Auto-submit when section timer reaches zero is handled inside the section timer useEffect above.

    const toggleFlagQuestion = (qId: string) => {
        const nextFlags = flaggedQuestionIds.includes(qId)
            ? flaggedQuestionIds.filter((id) => id !== qId)
            : [...flaggedQuestionIds, qId];
        setFlaggedQuestionIds(nextFlags);
        if (mockAttemptId) {
            localStorage.setItem(`mock_attempt_${mockAttemptId}_flags`, JSON.stringify(nextFlags));
        }
    };

    const markAudioAsPlayed = (qId: string) => {
        const nextPlayed = [...new Set([...playedAudioIds, qId])];
        setPlayedAudioIds(nextPlayed);
        if (mockAttemptId) {
            localStorage.setItem(`mock_attempt_${mockAttemptId}_played_audio`, JSON.stringify(nextPlayed));
        }
    };

    const changeSection = (idx: number) => {
        setCurrentSectionIndex(idx);
        if (mockAttemptId) {
            localStorage.setItem(`mock_attempt_${mockAttemptId}_section_idx`, String(idx));
        }
        setActiveQuestionIndex(0);
        setIsSectionGateOpen(selectedMockTest?.examType !== "JFT_BASIC");
    };

    const handleNextSectionOrSubmit = () => {
        if (currentSectionIndex < availableSections.length - 1) {
            setConfirmModalType("section");
        } else {
            setConfirmModalType("submit");
        }
    };

    const confirmProceedSection = async () => {
        if (currentSectionIndex < availableSections.length - 1) {
            // Save answers so far
            if (mockAttemptId && !isOffline) {
                try {
                    const payload = getAnswersPayload(mockAnswers);
                    await saveMockTestProgress(mockAttemptId, { answers: payload });
                } catch (err) {
                    console.error("Failed to save progress during section change:", err);
                }
            }
            changeSection(currentSectionIndex + 1);
            toast.success("Section completed. Proceeding to the next section.");
        }
    };

    const [actionPending, setActionPending] = useState(false);

    async function handleChangePosition(e: React.MouseEvent, testId: string, currentPos: number, offset: number) {
        e.stopPropagation();
        const newPos = currentPos + offset;
        if (newPos < 1) return;
        try {
            await updateMockTest(testId, { position: newPos });

            // Re-fetch tests implicitly by mutating local state or re-requesting
            const response = user?.role === "teacher"
                ? await getAssignedMockTests({ page: 1, limit: 100 })
                : await getMockTests({ page: 1, limit: 100 });
            setAllMockTests(response.items ?? []);

        } catch (err) {
            toast.error("Failed to update position");
        }
    }

    async function handleDeleteMockTest() {
        if (!selectedMockTestId) return;
        if (!window.confirm("Are you sure you want to permanently delete this mock test booklet and all its questions/attempts? This cannot be undone.")) return;
        setActionPending(true);
        try {
            await deleteMockTest(selectedMockTestId);
            toast.success("Mock test deleted successfully");
            setAllMockTests(prev => prev.filter(t => t.id !== selectedMockTestId));
            setSelectedMockTestId("");
        } catch (e: unknown) {
            toast.error(
                typeof e === "object" && e && "message" in e ? String((e as { message: unknown }).message) : "Failed to delete mock test"
            );
        } finally {
            setActionPending(false);
        }
    }

    async function handleDuplicateMockTest() {
        if (!selectedMockTestId) return;
        setActionPending(true);
        try {
            const duplicated = await duplicateMockTest(selectedMockTestId);
            toast.success("Mock test duplicated successfully!");
            setAllMockTests(prev => [...prev, duplicated]);
            setSelectedMockTestId(duplicated.id);
        } catch (e: unknown) {
            toast.error(
                typeof e === "object" && e && "message" in e ? String((e as { message: unknown }).message) : "Failed to duplicate mock test"
            );
        } finally {
            setActionPending(false);
        }
    }

    async function handleStartMockTest() {
        if (!selectedMockTestId) return;
        try {
            setMockResult(null);
            setShowAnswersReview(false);

            // Ensure questions are loaded and stored in memory before attempting answer mapping
            let currentQs = selectedQuestions;
            if (!currentQs || currentQs.length === 0) {
                currentQs = await getMockTestQuestions(selectedMockTestId);
                setSelectedQuestions(currentQs);
            }

            const a = await startMockTest(selectedMockTestId);

            // Sync clock skew
            if (a.serverTime) {
                clockSkewRef.current = new Date(a.serverTime).getTime() - Date.now();
            } else {
                clockSkewRef.current = 0;
            }

            setMockAttemptId(a.id);
            setMockAttempt(a);
            localStorage.setItem("active_mock_attempt_id", a.id);

            const localSavedAns = localStorage.getItem(`mock_attempt_${a.id}_answers`);
            const localAns = localSavedAns ? JSON.parse(localSavedAns) : {};
            const initial = (a.answers && typeof a.answers === "object" ? a.answers : {}) as Record<string, unknown>;
            const migrated: Record<string, unknown> = {};
            for (const q of currentQs) {
                const localVal = localAns[q.id];
                if (localVal !== undefined && localVal !== null) {
                    migrated[q.id] = localVal;
                } else {
                    const ansVal = initial[q.id];
                    if (ansVal !== undefined && ansVal !== null) {
                        migrated[q.id] = migrateLoadedMcqToIndex(q, ansVal);
                    }
                }
            }
            setMockAnswers(migrated);

            // Restore metadata from DB or local storage fallback
            let restoredSectionIndex = 0;
            if (initial.__current_section_index !== undefined) {
                restoredSectionIndex = Number(initial.__current_section_index);
            } else {
                const savedIdx = localStorage.getItem(`mock_attempt_${a.id}_section_idx`);
                restoredSectionIndex = savedIdx !== null ? Number(savedIdx) : 0;
            }
            setCurrentSectionIndex(restoredSectionIndex);

            let restoredQuestionIndex = 0;
            if (initial.__active_question_index !== undefined) {
                restoredQuestionIndex = Number(initial.__active_question_index);
            } else {
                const savedQIdx = localStorage.getItem(`mock_attempt_${a.id}_question_idx`);
                restoredQuestionIndex = savedQIdx !== null ? Number(savedQIdx) : 0;
            }
            setActiveQuestionIndex(restoredQuestionIndex);

            if (Array.isArray(initial.__flagged_question_ids)) {
                setFlaggedQuestionIds(initial.__flagged_question_ids as string[]);
            } else {
                const savedFlags = localStorage.getItem(`mock_attempt_${a.id}_flags`);
                setFlaggedQuestionIds(savedFlags ? JSON.parse(savedFlags) : []);
            }

            if (Array.isArray(initial.__played_audio_ids)) {
                setPlayedAudioIds(initial.__played_audio_ids as string[]);
            } else {
                const savedAudio = localStorage.getItem(`mock_attempt_${a.id}_played_audio`);
                setPlayedAudioIds(savedAudio ? JSON.parse(savedAudio) : []);
            }

            if (initial.__jft_listening_confirmed !== undefined) {
                setJftListeningConfirmed(Boolean(initial.__jft_listening_confirmed));
            } else {
                setJftListeningConfirmed(false);
            }

            // Restore section start timestamps
            let sectionStarts: Record<string, number> = {};
            if (initial.__section_starts && typeof initial.__section_starts === "object") {
                sectionStarts = initial.__section_starts as Record<string, number>;
                for (const [sec, ts] of Object.entries(sectionStarts)) {
                    const startKey = SECTION_START_STORAGE_KEY(a.id, sec);
                    localStorage.setItem(startKey, String(ts));
                }
            }

            // ── Key fix: skip section gate if student was already mid-exam ──
            // If we have a section start timestamp for the restored section, the student
            // had already pressed "Start Section" — skip the gate and drop them back in.
            const SECTION_NAMES_ORDER = ["VOCABULARY", "GRAMMAR", "READING", "LISTENING"] as const;
            const restoredSectionKey = SECTION_NAMES_ORDER[restoredSectionIndex] ?? "VOCABULARY";
            const hadPassedGate =
                sectionStarts[restoredSectionKey] !== undefined ||
                sectionStarts["JFT_GLOBAL"] !== undefined ||
                localStorage.getItem(SECTION_START_STORAGE_KEY(a.id, restoredSectionKey)) !== null ||
                localStorage.getItem(SECTION_START_STORAGE_KEY(a.id, "JFT_GLOBAL")) !== null;

            if (hadPassedGate && Object.keys(migrated).length > 0) {
                // Student was mid-exam — bypass the gate
                setIsSectionGateOpen(false);

                // Restore scroll position after a short delay for DOM to render
                const savedScroll =
                    (typeof initial.__scroll_position === "number" ? initial.__scroll_position : null) ??
                    Number(localStorage.getItem(`mock_attempt_${a.id}_scroll`) ?? "0");

                if (savedScroll > 0) {
                    setTimeout(() => {
                        const el = document.getElementById("questions-scroll-container");
                        if (el) el.scrollTop = savedScroll;
                    }, 200);
                }
            } else {
                setIsSectionGateOpen(selectedMockTest?.examType !== "JFT_BASIC");
            }

            toast.success(Object.keys(migrated).length ? "Resumed mock test" : "Mock test started");
        } catch (e: unknown) {
            toast.error(
                typeof e === "object" && e && "message" in e ? String((e as { message: unknown }).message) : "Could not start mock test",
            );
            // The most common failure here is "no remaining attempts" (e.g. the
            // last attempt was just used). Refresh access so the "Buy More
            // Attempts" panel appears immediately, without needing a page reload.
            // mockResult was already cleared above, so the panel will render.
            void refetchAccess();
        }
    }

    async function handleSubmitMockTest() {
        if (!mockAttemptId || !selectedMockTest) return;

        // Clear active attempt ID from local storage before submission
        localStorage.removeItem("active_mock_attempt_id");

        const answersPayload = normalizeMcqAnswersForApi(selectedQuestions, { ...mockAnswers });

        if (isOffline) {
            // If we are offline, save pending submission
            localStorage.setItem("pending_submit_attempt_id", mockAttemptId);
            toast.warning("You are offline. Your exam has ended. We will submit it automatically when connection is restored.", { duration: 10000 });
            setMockAttemptId(null);
            setMockAttempt(null);
            return;
        }

        try {
            const submitted = await submitMockTest(mockAttemptId, { answers: answersPayload });
            setMockResult(submitted);
            const pct =
                selectedMockTest.totalMarks > 0
                    ? Math.round((Number(submitted.score) / selectedMockTest.totalMarks) * 100)
                    : 0;
            setMyBestScores((prev) => ({
                ...prev,
                [selectedMockTestId]: Math.max(prev[selectedMockTestId] ?? 0, pct),
            }));

            // Reset attempt configuration and exit fullscreen mode
            setMockAttemptId(null);
            setMockAttempt(null);

            // Refresh purchase access status (attempts may have been consumed)
            void refetchAccess();

            toast.success(`Mock test submitted successfully! Score: ${pct}% (${submitted.score} marks)`);

        } catch (e: unknown) {
            toast.error(
                typeof e === "object" && e && "message" in e ? String((e as { message: unknown }).message) : "Submit failed",
            );
        }
    }

    const handleExitCbt = () => {
        setConfirmModalType("exit");
    };

    const confirmExitCbt = async () => {
        localStorage.removeItem("active_mock_attempt_id");

        if (!isOffline) {
            try {
                const payload = getAnswersPayload(mockAnswers);
                await saveMockTestProgress(mockAttemptId!, { answers: payload });
            } catch { }
        }

        setMockAttemptId(null);
        setMockAttempt(null);
        toast.info("Exam paused. You can resume at any time.");
    };

    // Auto-resume active attempt on mount
    useEffect(() => {
        const savedAttemptId = localStorage.getItem("active_mock_attempt_id");
        if (!savedAttemptId) return;

        (async () => {
            try {
                setLoadingQuestions(true);
                const a = await getMockTestAttempt(savedAttemptId);
                if (a && !a.submittedAt) {
                    // Verify that it is not expired
                    const ends = a.endsAt ? new Date(a.endsAt) : new Date(new Date(a.startedAt).getTime() + 60 * 60 * 1000);
                    if (ends > new Date()) {
                        // Load test questions
                        const qs = await getMockTestQuestions(a.mockTestId);
                        setSelectedQuestions(qs);
                        setSelectedMockTestId(a.mockTestId);

                        // Sync clock skew
                        if (a.serverTime) {
                            clockSkewRef.current = new Date(a.serverTime).getTime() - Date.now();
                        } else {
                            clockSkewRef.current = 0;
                        }

                        setMockAttemptId(a.id);
                        setMockAttempt(a);

                        const localSavedAns = localStorage.getItem(`mock_attempt_${a.id}_answers`);
                        const localAns = localSavedAns ? JSON.parse(localSavedAns) : {};
                        const initial = (a.answers && typeof a.answers === "object" ? a.answers : {}) as Record<string, unknown>;
                        const migrated: Record<string, unknown> = {};
                        for (const q of qs) {
                            const localVal = localAns[q.id];
                            if (localVal !== undefined && localVal !== null) {
                                migrated[q.id] = localVal;
                            } else {
                                const ansVal = initial[q.id];
                                if (ansVal !== undefined && ansVal !== null) {
                                    migrated[q.id] = migrateLoadedMcqToIndex(q, ansVal);
                                }
                            }
                        }
                        setMockAnswers(migrated);

                        let restoredSectionIdx = 0;
                        if (initial.__current_section_index !== undefined) {
                            restoredSectionIdx = Number(initial.__current_section_index);
                        } else {
                            const savedIdx = localStorage.getItem(`mock_attempt_${a.id}_section_idx`);
                            restoredSectionIdx = savedIdx !== null ? Number(savedIdx) : 0;
                        }
                        setCurrentSectionIndex(restoredSectionIdx);

                        let restoredQIdx = 0;
                        if (initial.__active_question_index !== undefined) {
                            restoredQIdx = Number(initial.__active_question_index);
                        } else {
                            const savedQIdx = localStorage.getItem(`mock_attempt_${a.id}_question_idx`);
                            restoredQIdx = savedQIdx !== null ? Number(savedQIdx) : 0;
                        }
                        setActiveQuestionIndex(restoredQIdx);

                        if (Array.isArray(initial.__flagged_question_ids)) {
                            setFlaggedQuestionIds(initial.__flagged_question_ids as string[]);
                        } else {
                            const savedFlags = localStorage.getItem(`mock_attempt_${a.id}_flags`);
                            setFlaggedQuestionIds(savedFlags ? JSON.parse(savedFlags) : []);
                        }

                        if (Array.isArray(initial.__played_audio_ids)) {
                            setPlayedAudioIds(initial.__played_audio_ids as string[]);
                        } else {
                            const savedAudio = localStorage.getItem(`mock_attempt_${a.id}_played_audio`);
                            setPlayedAudioIds(savedAudio ? JSON.parse(savedAudio) : []);
                        }

                        if (initial.__jft_listening_confirmed !== undefined) {
                            setJftListeningConfirmed(Boolean(initial.__jft_listening_confirmed));
                        } else {
                            setJftListeningConfirmed(false);
                        }

                        // Restore section start timestamps
                        let resumeSectionStarts: Record<string, number> = {};
                        if (initial.__section_starts && typeof initial.__section_starts === "object") {
                            resumeSectionStarts = initial.__section_starts as Record<string, number>;
                            for (const [sec, ts] of Object.entries(resumeSectionStarts)) {
                                const startKey = SECTION_START_STORAGE_KEY(a.id, sec);
                                localStorage.setItem(startKey, String(ts));
                            }
                        }

                        // ── Key fix: skip section gate if student was mid-exam on reload ──
                        // Determine if the student had already passed the gate for their section.
                        const sectionNames = ["VOCABULARY", "GRAMMAR", "READING", "LISTENING"] as const;
                        const restoredSectionName = sectionNames[restoredSectionIdx] ?? "VOCABULARY";
                        const hadPassedGateOnResume =
                            resumeSectionStarts[restoredSectionName] !== undefined ||
                            resumeSectionStarts["JFT_GLOBAL"] !== undefined ||
                            localStorage.getItem(SECTION_START_STORAGE_KEY(a.id, restoredSectionName)) !== null ||
                            localStorage.getItem(SECTION_START_STORAGE_KEY(a.id, "JFT_GLOBAL")) !== null;

                        if (hadPassedGateOnResume && Object.keys(migrated).length > 0) {
                            // Student was mid-exam — bypass section gate
                            setIsSectionGateOpen(false);

                            // Restore scroll position after DOM renders
                            const savedScrollPos =
                                (typeof initial.__scroll_position === "number" ? initial.__scroll_position : null) ??
                                Number(localStorage.getItem(`mock_attempt_${a.id}_scroll`) ?? "0");

                            if (savedScrollPos > 0) {
                                setTimeout(() => {
                                    const el = document.getElementById("questions-scroll-container");
                                    if (el) el.scrollTop = savedScrollPos;
                                }, 200);
                            }
                        } else {
                            setIsSectionGateOpen(selectedMockTest?.examType !== "JFT_BASIC");
                        }

                        toast.success("Resumed active exam session automatically.");
                    } else {
                        localStorage.removeItem("active_mock_attempt_id");
                        toast.warning("Time has expired! Submitting your exam automatically...", { duration: 5000 });
                        try {
                            const localSavedAns = localStorage.getItem(`mock_attempt_${a.id}_answers`);
                            const localAns = localSavedAns ? JSON.parse(localSavedAns) : {};
                            const initial = (a.answers && typeof a.answers === "object" ? a.answers : {}) as Record<string, unknown>;

                            const qs = await getMockTestQuestions(a.mockTestId);
                            const migrated: Record<string, unknown> = {};
                            for (const q of qs) {
                                const localVal = localAns[q.id];
                                if (localVal !== undefined && localVal !== null) {
                                    migrated[q.id] = localVal;
                                } else {
                                    const ansVal = initial[q.id];
                                    if (ansVal !== undefined && ansVal !== null) {
                                        migrated[q.id] = migrateLoadedMcqToIndex(q, ansVal);
                                    }
                                }
                            }

                            const answersPayload = normalizeMcqAnswersForApi(qs, migrated);
                            const submitted = await submitMockTest(a.id, { answers: answersPayload });
                            setMockResult(submitted);
                            setSelectedMockTestId(a.mockTestId);
                        } catch (err) {
                            console.error("Failed to auto-submit expired attempt:", err);
                        }
                    }
                } else {
                    localStorage.removeItem("active_mock_attempt_id");
                }
            } catch (err) {
                console.error("Failed to auto-resume active attempt:", err);
                localStorage.removeItem("active_mock_attempt_id");
            } finally {
                setLoadingQuestions(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const response =
                    user?.role === "teacher"
                        ? await getAssignedMockTests({ page: 1, limit: 100 })
                        : await getMockTests({ page: 1, limit: 100 });
                if (cancelled) return;
                setAllMockTests(response.items ?? []);
                const first = response.items?.[0];
                const activeAttemptId = localStorage.getItem("active_mock_attempt_id");
                if (first && !activeAttemptId && !queryTestId) {
                    setSelectedLevel("ALL");
                    setSelectedMockTestId(first.id);
                }
            } catch (e: unknown) {
                if (!cancelled) {
                    toast.error(
                        typeof e === "object" && e && "message" in e ? String((e as { message: unknown }).message) : "Failed to load mock tests",
                    );
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [user, queryTestId]);

    useEffect(() => {
        if (!selectedMockTestId) {
            setSelectedQuestions([]);
            return;
        }
        let cancelled = false;
        (async () => {
            setLoadingQuestions(true);
            const attemptIdParam = searchParams.get("attemptId");

            if (attemptIdParam) {
                try {
                    const historicalAttempt = await getMockTestAttempt(attemptIdParam);
                    if (historicalAttempt && historicalAttempt.mockTestId === selectedMockTestId) {
                        setMockAttemptId(historicalAttempt.id);
                        setMockAttempt(historicalAttempt);
                        setMockResult(historicalAttempt);
                        setMockAnswers(historicalAttempt.answers || {});
                        setShowAnswersReview(true);
                    } else if (historicalAttempt && historicalAttempt.mockTestId !== selectedMockTestId) {
                        // The attempt in the URL doesn't belong to the currently selected mock test.
                        // Clear the local state.
                        setMockAttemptId(null);
                        setMockAttempt(null);
                        setMockResult(null);
                        setShowAnswersReview(false);
                    }
                } catch (e) {
                    console.error("Failed to load historical mock attempt", e);
                }
            } else {
                const savedAttemptId = localStorage.getItem("active_mock_attempt_id");
                if (!savedAttemptId) {
                    setMockAttemptId(null);
                    setMockAttempt(null);
                    setMockAnswers({});
                    setMockResult(null);
                    setShowAnswersReview(false);
                }
            }

            try {
                const data = await getMockTestQuestions(selectedMockTestId);
                if (!cancelled) setSelectedQuestions(data);
            } catch (e: unknown) {
                if (!cancelled) {
                    toast.error(
                        typeof e === "object" && e && "message" in e ? String((e as { message: unknown }).message) : "Failed to load questions",
                    );
                }
            } finally {
                if (!cancelled) setLoadingQuestions(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [selectedMockTestId, user]);

    // Allow guests

    // Render variables for regular dashboard
    const levelPills = (
        <div className="flex gap-1.5 overflow-x-auto pb-2 scrollbar-none">
            {levels.map((lvl) => {
                const isSelected = lvl === selectedLevel;
                return (
                    <button
                        key={lvl}
                        type="button"
                        onClick={() => setSelectedLevel(lvl)}
                        className={cn(
                            "px-4 py-2 rounded-full text-xs font-semibold tracking-wider transition-all duration-200 shrink-0",
                            isSelected
                                ? "bg-primary text-primary-foreground shadow-sm"
                                : "bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground",
                        )}
                    >
                        {lvl}
                    </button>
                );
            })}
        </div>
    );

    const testPicker = (
        <div className="grid grid-cols-1 gap-3 overflow-y-auto p-4 content-start max-h-[70vh] lg:max-h-none lg:flex-1 lg:min-h-0">
            {filteredMockTests.map((test) => (
                <KCard
                    key={test.id}
                    hoverable
                    className={cn(
                        "p-4 cursor-pointer transition-colors border-2",
                        selectedMockTestId === test.id ? "border-primary bg-primary/5" : "border-transparent",
                    )}
                    onClick={() => {
                        setSelectedMockTestId(test.id);
                        if (searchParams.has("attemptId")) {
                            setSearchParams(prev => {
                                const next = new URLSearchParams(prev);
                                next.delete("attemptId");
                                next.set("testId", test.id);
                                return next;
                            }, { replace: true });
                        }
                        setShowAnswersReview(false);
                        setMockAttemptId(null);
                        setMockAttempt(null);
                        setMockResult(null);
                    }}
                >
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                            <GraduationCap className="text-primary" size={20} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-foreground text-sm leading-snug flex items-center gap-1.5 flex-wrap">
                                {isAdmin && test.position != null && (
                                    <div className="flex items-center gap-0.5 mr-1" onClick={(e) => e.stopPropagation()}>
                                        <button
                                            type="button"
                                            onClick={(e) => handleChangePosition(e, test.id, test.position ?? 1, -1)}
                                            className="p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                                        >
                                            <ChevronUp className="size-3" />
                                        </button>
                                        <span className="text-muted-foreground font-mono text-[10px] border bg-muted/30 px-1 rounded flex items-center justify-center min-w-[1.25rem]">
                                            {test.position}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={(e) => handleChangePosition(e, test.id, test.position ?? 1, 1)}
                                            className="p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                                        >
                                            <ChevronDown className="size-3" />
                                        </button>
                                    </div>
                                )}
                                <span>{test.title || `${formatMockTestLevelsForDisplay(test.level)} mock`}</span>
                                {showAuthoringLinks && (
                                    <span className={cn(
                                        "text-[9px] px-1.5 py-0.5 rounded font-bold tracking-wide uppercase font-sans",
                                        test.isPublished
                                            ? "bg-emerald-55/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200"
                                            : "bg-amber-55/10 text-amber-700 dark:text-amber-400 border border-amber-200"
                                    )}>
                                        {test.isPublished ? "Published" : "Draft"}
                                    </span>
                                )}
                            </h3>
                            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground mt-1">
                                <span className="flex items-center gap-1">
                                    <Clock size={12} /> {test.duration} min
                                </span>
                                <span>{test.questionCount ?? 0} qs</span>
                                <span className="text-[10px] uppercase tracking-wide">
                                    {formatMockTestLevelsForDisplay(test.level)}
                                </span>
                            </div>
                            {myBestScores[test.id] != null ? (
                                <div className="flex items-center gap-1.5 text-xs mt-1">
                                    <Award size={12} className="text-success" />
                                    <span className="text-muted-foreground">
                                        Best <span className="font-bold text-foreground">{myBestScores[test.id]}%</span>
                                    </span>
                                </div>
                            ) : null}
                            {test.canManageQuestions && isTeacher ? (
                                <p className="text-[11px] text-primary font-medium mt-1">You can edit this test</p>
                            ) : null}
                        </div>
                    </div>
                </KCard>
            ))}
            {!filteredMockTests.length ? (
                <KCard className="p-4 text-sm text-muted-foreground text-center">No mock tests matching the filters.</KCard>
            ) : null}
        </div>
    );

    const takeTestPanel =
        selectedMockTest ? (
            <div
                className="bg-white border border-stone-200 p-8 sm:p-10 shadow-sm rounded-2xl text-stone-900 flex flex-col"
            >

                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stone-200 pb-4 select-none mb-6">
                    <div className="space-y-1">
                        <div className="border border-stone-805 px-3 py-0.5 inline-block tracking-widest text-xs font-bold mb-1">
                            {selectedMockTest.examType === 'JFT_BASIC' ? 'JFT-Basic' : '日本語能力試験'}
                        </div>
                        <h2 className="font-bold text-stone-900 text-lg md:text-xl">
                            {selectedMockTest.title || `${formatMockTestLevelsForDisplay(selectedMockTest.level)} 模擬試験`}
                        </h2>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 font-sans select-none">
                        {showAuthoringLinks && canEditMockTest(selectedMockTest, isAdmin) ? (
                            <>
                                <Button variant="outline" size="sm" className="border-stone-300 text-stone-700 rounded-xl text-xs" asChild>
                                    <Link to={`/mock-tests/${selectedMockTestId}/edit`}>試験編集 / Edit Exam</Link>
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleDuplicateMockTest}
                                    disabled={actionPending}
                                    className="border-stone-300 text-stone-700 rounded-xl text-xs h-8"
                                >
                                    複製 / Duplicate
                                </Button>
                            </>
                        ) : null}
                        {isAdmin ? (
                            <Button
                                variant="destructive"
                                size="sm"
                                onClick={handleDeleteMockTest}
                                disabled={actionPending}
                                className="rounded-xl text-xs h-8 font-bold"
                            >
                                削除 / Delete
                            </Button>
                        ) : null}
                    </div>
                </div>

                {loadingQuestions ? (
                    <p className="text-sm text-stone-500 font-sans flex items-center gap-2">
                        <Loader2 className="animate-spin size-4" /> 読み込み中…
                    </p>
                ) : !selectedQuestions.length ? (
                    <p className="text-sm text-stone-500 font-sans">この試験にはまだ問題が登録されていません。</p>
                ) : isStudent && mockResult ? (
                    <>
                        {(() => {
                            const pct = selectedMockTest.totalMarks > 0
                                ? Math.round((Number(mockResult.score) / selectedMockTest.totalMarks) * 100)
                                : 0;

                            const overallPassMark = selectedMockTest.passingMarks || Math.ceil(selectedMockTest.totalMarks * 0.6);
                            const achievedOverall = Number(mockResult.score) >= overallPassMark;
                            const isSectionalPassRequired = currentExamConfig?.passCriteria === "SECTIONAL_REQUIRED";

                            let failedSectionNames: string[] = [];
                            let mappedSections: any[] = [];

                            Object.entries(sectionBreakdown).forEach(([sec, stats]) => {
                                if (stats.maxScore > 0) {
                                    const secPct = stats.maxScore > 0 ? Math.round((stats.score / stats.maxScore) * 100) : 0;

                                    const configuredPassMark = selectedMockTest.categoryPassMarks?.[sec] ?? 0;
                                    const catPassMark = configuredPassMark > 0 ? configuredPassMark : Math.ceil(stats.maxScore * 0.3);
                                    const isSectionPass = stats.maxScore > 0 && stats.score >= catPassMark;

                                    if (isSectionalPassRequired && !isSectionPass) {
                                        failedSectionNames.push(getSectionDisplay(sec, true));
                                    }

                                    // Use getSectionDisplay() which queries the exam config for the correct
                                    // section name — avoids hardcoded if-else chains that miss dynamic section codes.
                                    const engName = getSectionDisplay(sec, false);
                                    const jpName = getSectionDisplay(sec, true);
                                    const label = `${jpName} (${engName})`;
                                    const origName = jpName;

                                    mappedSections.push({
                                        name: label,
                                        originalName: origName,
                                        correct: stats.correct,
                                        incorrect: stats.incorrect,
                                        skipped: stats.skipped,
                                        score: stats.score,
                                        maxScore: stats.maxScore,
                                        percentage: secPct,
                                        passMark: isSectionalPassRequired && catPassMark > 0 ? catPassMark : undefined,
                                        isPassed: isSectionalPassRequired ? isSectionPass : undefined
                                    });
                                }
                            });

                            let failureNotice = null;
                            if (isSectionalPassRequired && failedSectionNames.length > 0) {
                                failureNotice = "一部のセクションで基準点を満たしていません。 (Some sections did not meet the required pass mark.)";
                            }

                            // Calculate overall counts
                            let answeredCount = 0;
                            let skippedCount = 0;
                            const userAnswers = (mockResult?.answers as Record<string, unknown>) || {};
                            selectedQuestions.forEach(q => {
                                if (userAnswers[q.id] !== undefined && userAnswers[q.id] !== "") {
                                    answeredCount++;
                                } else {
                                    skippedCount++;
                                }
                            });

                            const timeTakenMinutes = mockResult.startedAt && mockResult.submittedAt
                                ? Math.round((new Date(mockResult.submittedAt).getTime() - new Date(mockResult.startedAt).getTime()) / 60000)
                                : null;

                            return (
                                <ExamResultReport
                                    examName={selectedMockTest.title}
                                    examTypeLabel={currentExamConfig?.name || selectedMockTest.examType || "JLPT"}
                                    candidateId={mockResult.id}
                                    candidateName={user?.firstName ? `${user.firstName} ${user.lastName}` : "Student"}
                                    completionDate={new Date(mockResult.submittedAt || new Date())}
                                    isPassed={mockResult.isPassed}
                                    totalScore={selectedMockTest.totalMarks}
                                    obtainedScore={Number(mockResult.score)}
                                    percentage={pct}
                                    passingScore={overallPassMark}
                                    timeTakenMinutes={timeTakenMinutes}
                                    totalQuestions={selectedQuestions.length}
                                    answeredCount={answeredCount}
                                    skippedCount={skippedCount}
                                    overallAccuracy={pct}
                                    sections={mappedSections}
                                    failureNotice={failureNotice}
                                    actions={
                                        <>
                                            <Button
                                                type="button"
                                                className="bg-stone-900 hover:bg-stone-800 text-white rounded-xl px-6 text-xs h-10 font-bold border border-stone-950 shadow-xs"
                                                onClick={() => setShowAnswersReview(true)}
                                            >
                                                解答解説を見る / Review Exam
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                className="border-stone-300 text-stone-700 rounded-xl px-6 text-xs h-10 font-bold hover:bg-stone-50"
                                                onClick={() => void handleStartMockTest()}
                                            >
                                                もう一度受験する / Re-attempt Test
                                            </Button>
                                        </>
                                    }
                                />
                            );
                        })()}
                    </>
                ) : (isStudent || !user) ? (

                    // =========================================================================
                    // MOCK TEST BOOKLET COVER RULES
                    // =========================================================================
                    <div className="space-y-6">
                        {/* Metadata Info Panel */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-center border-t border-b border-stone-200 py-6 my-6 font-sans">
                            <div className="space-y-1">
                                <span className="text-[10px] text-stone-500 uppercase tracking-widest font-semibold block">試験時間 / Duration</span>
                                <strong className="text-stone-900 text-lg md:text-xl font-bold">{selectedMockTest.duration} 分 (Minutes)</strong>
                            </div>
                            <div className="space-y-1 border-l border-stone-200">
                                <span className="text-[10px] text-stone-500 uppercase tracking-widest font-semibold block">問題数 / Questions</span>
                                <strong className="text-stone-900 text-lg md:text-xl font-bold">{selectedQuestions.length} 問 (Items)</strong>
                            </div>
                            <div className="space-y-1 border-l border-stone-200 col-span-2 sm:col-span-1">
                                <span className="text-[10px] text-stone-500 uppercase tracking-widest font-semibold block">総合得点合格点 / Pass Mark</span>
                                <strong className="text-stone-950 text-lg md:text-xl font-bold">
                                    {selectedMockTest.passingMarks || 0} / {selectedMockTest.totalMarks} 点
                                </strong>
                            </div>
                        </div>

                        {/* Pass Marks Criteria Panel */}
                        {selectedMockTest.categoryPassMarks && Object.values(selectedMockTest.categoryPassMarks).some(v => v > 0) && (
                            <div className="border border-stone-200 p-5 space-y-3 text-xs text-stone-700 font-sans leading-relaxed select-none bg-stone-50/50 rounded-xl">
                                <h3 className="text-xs font-bold text-stone-900 border-b border-stone-200 pb-2 text-center font-sans">
                                    基準点 (最低必要得点) / SECTIONAL PASS MARKS
                                </h3>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                                    {Object.keys(sectionBreakdown).map(sec => {
                                        const passVal = selectedMockTest.categoryPassMarks?.[sec] ?? 0;
                                        if (!passVal) return null;
                                        const jp = selectedMockTest?.examType === "JFT_BASIC"
                                            ? (sec === "VOCABULARY" ? "文字・語彙" :
                                                sec === "GRAMMAR" ? "会話・表現" :
                                                    sec === "READING" ? "読解" : "聴解")
                                            : (sec === "VOCABULARY" ? "文字・語彙" :
                                                sec === "GRAMMAR" ? "文法" :
                                                    sec === "READING" ? "読解" : "聴解");
                                        return (
                                            <div key={sec} className="bg-white p-2.5 rounded-lg border border-stone-200 flex flex-col justify-center">
                                                <span className="text-[10px] text-stone-550 block font-semibold">{jp}</span>
                                                <strong className="text-stone-950 font-mono text-sm mt-0.5">{passVal} 点</strong>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Instruction Warning Box */}
                        <div className="border border-stone-200 p-5 space-y-4 text-xs text-stone-700 font-sans leading-relaxed select-none bg-stone-50/50 rounded-xl">
                            <h3 className="text-sm font-bold text-stone-900 border-b border-stone-200 pb-2 text-center font-sans">
                                受験者注意事項 / MOCK TEST RULES
                            </h3>
                            {currentExamConfig?.mockTestRules ? (
                                <div
                                    className="prose prose-sm max-w-none text-stone-700 prose-p:my-1 prose-ul:my-1 prose-li:my-0.5"
                                    dangerouslySetInnerHTML={{ __html: currentExamConfig.mockTestRules }}
                                />
                            ) : currentExamConfig?.name === "JFT-Basic" ? (
                                <div>
                                    <ul className="list-decimal pl-5 space-y-2.5">
                                        <li>
                                            総合得点が200点以上で「一定の基準を満たす（A2レベル）」と判定されます。<br />
                                            <strong>Evaluation:</strong> An overall score of 200 or higher is required to pass (A2 Level).
                                        </li>
                                        <li>
                                            セクションごとの基準点（足切り）はありません。<br />
                                            <strong>No Sectional Pass Marks:</strong> There are no minimum passing scores required for individual sections.
                                        </li>
                                        <li>
                                            試験時間中は、すべての問題の間を自由に行き来でき、解答を変更できます。<br />
                                            <strong>Free Navigation:</strong> You can freely navigate between all questions and change your answers at any time during the exam.
                                        </li>
                                    </ul>
                                </div>
                            ) : (
                                <div>
                                    <ul className="list-decimal pl-5 space-y-2.5">
                                        <li>
                                            この模擬試験はセクション（文字・語彙、文法、読解、聴解）に分かれています。<br />
                                            <strong>Sectioned Structure:</strong> This mock test is divided into distinct sections.
                                        </li>
                                        <li>
                                            セクションを終了して次へ進むと、元のセクションに戻ることはできません。<br />
                                            <strong>No Going Back:</strong> Once you submit and proceed to the next section, you cannot return to edit previous answers.
                                        </li>
                                        <li>
                                            制限時間はセクションごとに独立しており、時間が来ると自動的に提出され次へ進みます。<br />
                                            <strong>Independent Timers:</strong> Each section has its own independent timer.
                                        </li>
                                    </ul>
                                </div>
                            )}
                        </div>

                        {/* Purchase / Start Exam Button */}
                        {(() => {
                            if (!user) {
                                const isFree = !selectedMockTest?.price || Number(selectedMockTest.price) === 0;
                                return (
                                    <div className="pt-2">
                                        <Button
                                            onClick={() => {
                                                toast.info("Please login to proceed with this mock test.");
                                                navigate(`/login?returnTo=${encodeURIComponent(`/mock-tests?testId=${selectedMockTest.id}`)}`);
                                            }}
                                            className="w-full sm:w-auto bg-primary text-primary-foreground font-bold px-8 h-11 text-xs rounded-xl shadow-md"
                                        >
                                            {isFree ? "Login to Start Mock Test" : "Login to Purchase Mock Test"}
                                        </Button>
                                    </div>
                                );
                            }
                            if (!isStudent) return null;
                            const isFree = !selectedMockTest?.price || Number(selectedMockTest.price) === 0;
                            const status = accessStatus?.status;

            // Not purchased separately, but an all-access subscription is active.
                            if (status === "subscribed") {
                                return (
                                    <div className="space-y-2">
                                        <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-center">
                                            <p className="text-[11px] font-semibold text-indigo-800">
                                                全アクセス定期購入中 / All-Access Subscription Active
                                            </p>
                                            <p className="text-[10px] text-indigo-600">
                                                Unlimited attempts on every mock test
                                                {accessStatus?.subscription?.expiresAt
                                                    ? ` · until ${new Date(accessStatus.subscription.expiresAt).toLocaleDateString()}`
                                                    : ""}
                                            </p>
                                        </div>
                                        <Button
                                            onClick={() => void handleStartMockTest()}
                                            className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800 text-white rounded-xl border border-slate-950 font-bold px-8 h-11 select-none font-sans text-xs shadow-sm transition-all"
                                        >
                                            試験を開始する / Start Timed Mock Test
                                        </Button>
                                    </div>
                                );
                            }

                            // Free test or access granted (with attempts remaining)
                            if (isFree || status === "free" || (status === "purchased" && (accessStatus?.remainingAttempts ?? 0) > 0)) {
                                const remaining = status === "purchased" ? accessStatus?.remainingAttempts : undefined;
                                return (
                                    <div className="space-y-2">
                                        {remaining !== undefined && remaining > 0 && (
                                            <p className="text-[10px] text-stone-500 text-center font-sans">
                                                残り受験回数 / Remaining Attempts: <strong className="text-stone-900">{remaining}</strong>
                                            </p>
                                        )}
                                        <Button
                                            onClick={() => void handleStartMockTest()}
                                            className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800 text-white rounded-xl border border-slate-950 font-bold px-8 h-11 select-none font-sans text-xs shadow-sm transition-all"
                                        >
                                            試験を開始する / Start Timed Mock Test
                                        </Button>
                                    </div>
                                );
                            }

                            // Not purchased
                            // A refunded purchase is treated like never-purchased:
                            // access is gone and the student may buy again (the
                            // backend re-activates the row on re-purchase).
                            if (status === "not_purchased" || status === "refunded") {
                                return (
                                    <div className="border border-stone-200 rounded-xl p-5 bg-stone-50 space-y-3 text-center">
                                        <div>
                                            <div className="text-[10px] text-stone-500 uppercase tracking-widest font-semibold">
                                                この試験を受験するには購入が必要です / Purchase Required
                                            </div>
                                            <div className="text-2xl font-bold font-mono text-stone-900 mt-1">
                                                {appliedMockCoupon
                                                    ? <><span className="line-through text-stone-400 text-base">{accessStatus?.mockTest?.currency ?? "BDT"} {Number(selectedMockTest?.price ?? 0).toLocaleString()}</span><br /><span className="text-emerald-700">{accessStatus?.mockTest?.currency ?? "BDT"} {appliedMockCoupon.finalPrice.toLocaleString()}</span></>
                                                    : <>{accessStatus?.mockTest?.currency ?? "BDT"} {Number(selectedMockTest?.price ?? 0).toLocaleString()}</>
                                                }
                                            </div>
                                            <div className="text-[10px] text-stone-400">
                                                {unitPrice > 0
                                                    ? <>{accessStatus?.mockTest?.currency ?? "BDT"} {unitPrice.toLocaleString()} per attempt</>
                                                    : <>{attemptCap} attempt{attemptCap !== 1 ? "s" : ""} included</>}
                                            </div>
                                        </div>

                                        {/* Attempt quantity selector (per-attempt pricing). */}
                                        {unitPrice > 0 && attemptCap > 1 && (
                                            <div className="rounded-lg border border-stone-200 bg-white px-3 py-2">
                                                <div className="text-[10px] text-stone-500 uppercase tracking-widest font-semibold mb-1.5">
                                                    受験回数を選択 / How many attempts?
                                                </div>
                                                <div className="flex items-center justify-center gap-3">
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        className="h-8 w-8 p-0 rounded-lg"
                                                        disabled={purchaseQuantity <= 1}
                                                        onClick={() => setPurchaseQuantity((q) => Math.max(1, q - 1))}
                                                    >−</Button>
                                                    <span className="text-lg font-bold font-mono w-8 text-center">{purchaseQuantity}</span>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        className="h-8 w-8 p-0 rounded-lg"
                                                        disabled={purchaseQuantity >= attemptCap}
                                                        onClick={() => setPurchaseQuantity((q) => Math.min(attemptCap, q + 1))}
                                                    >+</Button>
                                                </div>
                                                <div className="text-center mt-1.5 text-[11px] text-stone-600">
                                                    Total: <strong className="font-mono">{accessStatus?.mockTest?.currency ?? "BDT"} {(unitPrice * purchaseQuantity).toLocaleString()}</strong>
                                                    <span className="text-stone-400"> (max {attemptCap})</span>
                                                </div>
                                            </div>
                                        )}

                                        {/* Coupon input for paid mock tests */}
                                        {user && user.role === "student" && selectedMockTestId && Number(selectedMockTest?.price ?? 0) > 0 && (
                                            <CouponInput
                                                productType="MOCK_TEST"
                                                productId={selectedMockTestId}
                                                originalPrice={Number(selectedMockTest?.price ?? 0)}
                                                currency={accessStatus?.mockTest?.currency ?? "BDT"}
                                                onCouponApplied={(result) => setAppliedMockCoupon(result)}
                                                onCouponCleared={() => setAppliedMockCoupon(null)}
                                            />
                                        )}
                                        {/* Mandatory policy acceptance before purchase */}
                                        <PolicyAcceptance
                                            id="policy-accept-mocktest"
                                            checked={mockPolicyAccepted}
                                            onChange={setMockPolicyAccepted}
                                            className="justify-center"
                                        />
                                        <Button
                                            className="bg-primary text-primary-foreground rounded-xl font-bold px-8 h-10 text-xs"
                                            disabled={purchasingId === selectedMockTestId || !mockPolicyAccepted}
                                            onClick={async () => {
                                                if (!selectedMockTestId) return;
                                                try {
                                                    setPurchasingId(selectedMockTestId);
                                                    const res = await initMockTestBkashPayment(selectedMockTestId, appliedMockCoupon?.couponCode, purchaseQuantity);
                                                    if (res.bkashURL) {
                                                        toast.success("Redirecting to bKash payment...");
                                                        window.location.href = res.bkashURL;
                                                        return;
                                                    }
                                                    await refetchAccess();
                                                    // Fetch questions immediately on purchase so the page updates with the correct question list/count
                                                    const qs = await getMockTestQuestions(selectedMockTestId);
                                                    setSelectedQuestions(qs);
                                                    toast.success("Access granted! You can now start the exam.");
                                                } catch (e: any) {
                                                    toast.error(e?.message ?? "Purchase failed");
                                                } finally {
                                                    setPurchasingId(null);
                                                }
                                            }}
                                        >
                                            {purchasingId === selectedMockTestId
                                                ? <Loader2 className="animate-spin size-3.5 mr-1" />
                                                : null
                                            }
                                            {unitPrice > 0 && purchaseQuantity > 1
                                                ? `${purchaseQuantity}回分を購入 / Buy ${purchaseQuantity} Attempts`
                                                : "受験権を購入する / Purchase Access"}
                                        </Button>
                                        {!mockPolicyAccepted && (
                                            <p className="text-[10px] text-stone-500">
                                                Please accept the policies above to continue.
                                            </p>
                                        )}
                                        <p className="text-[9px] text-stone-400">
                                            Contact your administrator if you believe you should have access.
                                        </p>
                                    </div>
                                );
                            }

                            // Expired
                            if (status === "expired") {
                                return (
                                    <div className="border border-rose-200 rounded-xl p-4 bg-rose-50 text-center space-y-2">
                                        <AlertCircle className="size-6 text-rose-500 mx-auto" />
                                        <p className="text-xs font-semibold text-rose-800">Your access has expired.</p>
                                        <p className="text-[10px] text-rose-600">Contact your administrator to renew access or purchase again.</p>
                                    </div>
                                );
                            }

                            // No remaining attempts → let the student buy more.
                            if (status === "purchased" && (accessStatus?.remainingAttempts ?? 0) <= 0) {
                                return (
                                    <div className="border border-amber-200 rounded-xl p-5 bg-amber-50 text-center space-y-3">
                                        <div>
                                            <AlertCircle className="size-6 text-amber-500 mx-auto" />
                                            <p className="text-xs font-semibold text-amber-800 mt-1">受験回数がなくなりました / No Remaining Attempts</p>
                                            <p className="text-[10px] text-amber-600">You've used all your attempts. Buy more to keep practising.</p>
                                        </div>

                                        {/* Quantity selector for buying more attempts. */}
                                        {unitPrice > 0 && (
                                            <div className="rounded-lg border border-amber-200 bg-white px-3 py-2">
                                                <div className="flex items-center justify-center gap-3">
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        className="h-8 w-8 p-0 rounded-lg"
                                                        disabled={purchaseQuantity <= 1}
                                                        onClick={() => setPurchaseQuantity((q) => Math.max(1, q - 1))}
                                                    >−</Button>
                                                    <span className="text-lg font-bold font-mono w-8 text-center">{purchaseQuantity}</span>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        className="h-8 w-8 p-0 rounded-lg"
                                                        disabled={purchaseQuantity >= attemptCap}
                                                        onClick={() => setPurchaseQuantity((q) => Math.min(attemptCap, q + 1))}
                                                    >+</Button>
                                                </div>
                                                <div className="text-center mt-1.5 text-[11px] text-stone-600">
                                                    Total: <strong className="font-mono">{accessStatus?.mockTest?.currency ?? "BDT"} {(unitPrice * purchaseQuantity).toLocaleString()}</strong>
                                                    <span className="text-stone-400"> (max {attemptCap})</span>
                                                </div>
                                            </div>
                                        )}

                                        <Button
                                            className="bg-primary text-primary-foreground rounded-xl font-bold px-8 h-10 text-xs"
                                            disabled={purchasingId === selectedMockTestId}
                                            onClick={async () => {
                                                if (!selectedMockTestId) return;
                                                try {
                                                    setPurchasingId(selectedMockTestId);
                                                    const res = await initMockTestBkashPayment(selectedMockTestId, undefined, purchaseQuantity);
                                                    if (res.bkashURL) {
                                                        toast.success("Redirecting to bKash payment...");
                                                        window.location.href = res.bkashURL;
                                                        return;
                                                    }
                                                    await refetchAccess();
                                                    toast.success("More attempts added! You can start again.");
                                                } catch (e: any) {
                                                    toast.error(e?.message ?? "Purchase failed");
                                                } finally {
                                                    setPurchasingId(null);
                                                }
                                            }}
                                        >
                                            {purchasingId === selectedMockTestId
                                                ? <Loader2 className="animate-spin size-3.5 mr-1" />
                                                : null}
                                            受験回数を追加購入 / Buy More Attempts
                                        </Button>
                                    </div>
                                );
                            }

                            // Loading / fallback: show disabled button
                            return (
                                <Button
                                    onClick={() => void handleStartMockTest()}
                                    className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800 text-white rounded-xl border border-slate-950 font-bold px-8 h-11 select-none font-sans text-xs shadow-sm transition-all"
                                >
                                    試験を開始する / Start Timed Mock Test
                                </Button>
                            );
                        })()}

                        {/* All-access subscription promo — hidden when already subscribed. */}
                        {isStudent && !mySubscription?.active && accessStatus?.status !== "subscribed" && (subscriptionPlans?.length ?? 0) > 0 && (
                            <div className="mt-4 rounded-xl border border-indigo-200 bg-gradient-to-b from-indigo-50 to-white p-4 space-y-3">
                                <div className="text-center">
                                    <p className="text-[11px] font-bold text-indigo-900 uppercase tracking-widest">
                                        全アクセスパス / All-Access Pass
                                    </p>
                                    <p className="text-[10px] text-indigo-600">
                                        Unlimited attempts on every mock test for one price.
                                    </p>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    {subscriptionPlans!.map((plan) => (
                                        <button
                                            key={plan.id}
                                            type="button"
                                            disabled={subscribingPlan !== null}
                                            onClick={() => void handleSubscribe(plan)}
                                            className="rounded-lg border border-indigo-200 bg-white px-3 py-2 text-center hover:border-indigo-400 hover:bg-indigo-50 transition-colors disabled:opacity-50"
                                        >
                                            <div className="text-[11px] font-semibold text-stone-800">
                                                {plan.title || `${plan.durationMonths} month${plan.durationMonths !== 1 ? "s" : ""}`}
                                            </div>
                                            <div className="text-sm font-bold font-mono text-indigo-700">
                                                {plan.currency} {Number(plan.price).toLocaleString()}
                                            </div>
                                            {subscribingPlan === plan.id && (
                                                <Loader2 className="animate-spin size-3.5 mx-auto mt-1 text-indigo-500" />
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="space-y-4 font-sans text-xs text-stone-500 select-none">
                        <p className="border border-dashed border-stone-300 rounded p-3 bg-stone-50">
                            プレビューモードです。模擬試験を実行してタイマーや成績保存を有効にするには、学生アカウントでログインしてください。
                        </p>
                        <div className="max-h-[300px] overflow-y-auto space-y-3" >
                            {selectedQuestions.slice(0, 10).map((q, idx) => (
                                <div key={q.id} className="p-3 border border-stone-200 bg-white flex justify-between items-center text-xs">
                                    <span>Q{idx + 1}: {renderFurigana(q.questionText)}</span>
                                    <span className="text-stone-500 font-semibold font-sans">{getQuestionCategory(q, currentExamConfig)}</span>
                                </div>
                            ))}
                            {selectedQuestions.length > 10 && (
                                <p className="text-[10px] text-stone-400 text-center font-sans">... 他 {selectedQuestions.length - 10} 問の問題があります。</p>
                            )}
                        </div>
                    </div>
                )}
            </div>
        ) : (
            <div className="bg-white border border-stone-300 p-8 text-center text-stone-500 text-sm" >
                左側のリストから模擬試験を選択してください。
            </div>
        );

    const groupedSections = useMemo(() => {
        const currentSec = availableSections[currentSectionIndex] || "VOCABULARY";
        const catQs = selectedQuestions.filter(q => getQuestionCategory(q, currentExamConfig) === currentSec);

        const isJFTStyle = currentExamConfig?.passCriteria !== "SECTIONAL_REQUIRED";

        return [{
            category: currentSec,
            label: isJFTStyle
                ? `${getSectionDisplay(currentSec, true)} (${getSectionDisplay(currentSec, false)})`
                : getSectionDisplay(currentSec, true),
            groups: groupMockQuestions(catQs)
        }];
    }, [selectedMockTest, selectedQuestions, availableSections, currentSectionIndex, currentExamConfig, getSectionDisplay]);

    // CBT Mode Fullscreen Render
    if (mockAttemptId && !mockResult) {

        return (
            <ExamSecurityWrapper>
                <div className="fixed inset-0 z-50 bg-[#edece9] flex flex-col overflow-hidden font-sans text-stone-900 select-none">

                    {/* Equalizer animation and inline styles */}
                    <style dangerouslySetInnerHTML={{
                        __html: `
          @keyframes eq-pulse {
            0%, 100% { transform: scaleY(0.2); }
            50% { transform: scaleY(1); }
          }
          .animate-eq-1 { animation: eq-pulse 0.6s ease-in-out infinite; }
          .animate-eq-2 { animation: eq-pulse 0.8s ease-in-out infinite; animation-delay: 0.1s; }
          .animate-eq-3 { animation: eq-pulse 0.5s ease-in-out infinite; animation-delay: 0.2s; }
          .animate-eq-4 { animation: eq-pulse 0.7s ease-in-out infinite; animation-delay: 0.15s; }
          .animate-eq-5 { animation: eq-pulse 0.9s ease-in-out infinite; animation-delay: 0.25s; }
        ` }} />

                    {/* Audio element — protected: no download controls, no context menu */}
                    <div onContextMenu={(e) => e.preventDefault()}>
                        <audio
                            ref={audioRef}
                            className="hidden"
                            controlsList="nodownload nofullscreen noremoteplayback"
                            disablePictureInPicture
                            onPlay={() => setAudioPlaying(true)}
                            onPause={() => setAudioPlaying(false)}
                            onEnded={() => {
                                setAudioPlaying(false);
                                if (playingAudioId) {
                                    markAudioAsPlayedForId(playingAudioId);
                                }
                                setPlayingAudioId(null);
                            }}
                            onLoadedMetadata={(e) => setAudioDuration(e.currentTarget.duration)}
                            onTimeUpdate={(e) => setAudioCurrentTime(e.currentTarget.currentTime)}
                        />
                    </div>

                    {/* Top Status Header */}
                    <header className="h-14 border-b border-slate-800 px-3 md:px-6 flex items-center justify-between bg-slate-900 text-slate-100 shadow-sm shrink-0 select-none">
                        <div className="flex items-center gap-2 md:gap-4 truncate">
                            <div className="flex items-center gap-1.5 md:gap-2 truncate">
                                <span className="font-bold text-sm md:text-base tracking-wide text-slate-100 truncate max-w-[120px] sm:max-w-none">
                                    {currentExamConfig?.name || (selectedMockTest?.examType === "JFT_BASIC" ? "JFT-Basic" : "JLPT")}
                                </span>
                                <span className="text-xs text-slate-700 hidden sm:inline">|</span>
                                <span className="font-semibold text-[10px] md:text-xs text-slate-400 tracking-wider uppercase font-sans truncate hidden sm:inline">
                                    {selectedMockTest?.title || "MOCK ASSESSMENT"}
                                </span>
                            </div>
                        </div>

                        {/* Section Breadcrumbs */}
                        <div className="hidden md:flex items-center gap-2 font-sans select-none">
                            {selectedMockTest?.examType === "JFT_BASIC" ? (
                                <div className="flex items-center gap-2 px-3 py-1 bg-slate-800 text-slate-200 rounded border border-slate-700 shadow-xs text-xs font-bold tracking-wider">
                                    <span className="text-amber-400">Section {currentSectionIndex + 1} of {availableSections.length}</span>
                                    <span className="text-slate-500">|</span>
                                    <span>
                                        {getSectionDisplay(activeSection, true)}
                                    </span>
                                </div>
                            ) : (
                                availableSections.map((sec, idx) => {
                                    const isCompleted = idx < currentSectionIndex;
                                    const isActive = idx === currentSectionIndex;

                                    return (
                                        <div
                                            key={sec}
                                            className={cn(
                                                "flex items-center gap-1.5 px-3 py-1 rounded border text-[10px] font-bold tracking-wider transition-all",
                                                isActive
                                                    ? "bg-slate-800 text-white border-slate-700 shadow-xs"
                                                    : isCompleted
                                                        ? "bg-emerald-950/40 text-emerald-400 border-emerald-900/60"
                                                        : "bg-slate-900 text-slate-500 border-slate-800"
                                            )}
                                        >
                                            {isCompleted && <Check size={10} className="mr-0.5" />}
                                            <span>
                                                {isCompleted && isActive === false
                                                    ? getSectionDisplay(sec, true)
                                                    : getSectionDisplay(sec, true)}
                                            </span>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        <div className="flex items-center gap-2 md:gap-4 font-sans select-none">
                            {/* Section-Level Countdown Timer */}
                            {sectionTimeLabel !== null && !isSectionGateOpen && (
                                <span className={cn(
                                    "inline-flex items-center gap-1.5 md:gap-2 font-mono text-sm font-bold tracking-wide tabular-nums px-2.5 md:px-3 py-1 rounded-full border shadow-inner transition-colors",
                                    sectionSecondsLeft !== null && sectionSecondsLeft < 300
                                        ? "bg-red-950/30 border-red-800 text-red-400 animate-pulse font-extrabold"
                                        : "bg-slate-800 border-slate-700 text-amber-400"
                                )}>
                                    <Clock size={13} className="shrink-0 text-current" />
                                    <span className="text-[9px] uppercase font-sans font-semibold opacity-75 md:mr-0.5 hidden sm:inline">
                                        {selectedMockTest?.examType === "JFT_BASIC"
                                            ? "試験"
                                            : getSectionDisplay(activeSection, true)}
                                    </span>
                                    {sectionTimeLabel}
                                </span>
                            )}

                            {/* Status Indicator */}
                            {isOffline ? (
                                <span className="hidden sm:inline-flex items-center gap-1.5 bg-amber-950/30 border border-amber-800/50 text-amber-400 text-[10px] px-2.5 py-1 rounded-full font-semibold font-sans">
                                    <AlertCircle size={11} className="text-amber-400" /> Offline
                                </span>
                            ) : isSyncing ? (
                                <span className="hidden sm:inline-flex items-center gap-1.5 text-slate-400 text-[10px] font-sans">
                                    <Loader2 size={11} className="animate-spin text-slate-500" /> Saving
                                </span>
                            ) : (
                                <span className="hidden sm:inline-flex items-center gap-1.5 text-emerald-400 text-[10px] font-sans">
                                    <Check size={11} className="text-emerald-500" /> Saved
                                </span>
                            )}

                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="text-slate-300 border-slate-700 bg-slate-800 hover:bg-slate-700 hover:text-white text-xs h-8 px-2 md:px-3 font-sans font-medium rounded-lg"
                                onClick={handleExitCbt}
                            >
                                <LogOut size={14} className="sm:mr-1.5" /> <span className="hidden sm:inline">一時退室</span>
                            </Button>
                        </div>
                    </header>

                    {/* Section Progress Bar */}
                    {!isSectionGateOpen && (
                        <Progress value={progressPercentSection} className="h-1 bg-slate-800 rounded-none shrink-0" />
                    )}

                    {/* Main Workspace Frame */}
                    <div className="flex-1 flex overflow-hidden">

                        {isSectionGateOpen ? (
                            /* Interstitial Section Gate */
                            <div className="flex-1 flex flex-col justify-center items-center p-4 sm:p-6 bg-[#edece9] overflow-y-auto">
                                <div className="w-full max-w-lg bg-white border border-stone-200 rounded-2xl p-6 sm:p-10 shadow-md relative" >
                                    <div className="text-center space-y-4 mb-6 select-none">
                                        <div className={cn(
                                            "w-14 h-14 rounded-full flex items-center justify-center mx-auto text-white shadow-xs",
                                            activeSection === "VOCABULARY" ? "bg-blue-600" :
                                                activeSection === "GRAMMAR" ? "bg-amber-600" :
                                                    activeSection === "READING" ? "bg-emerald-600" : "bg-purple-600"
                                        )}>
                                            {activeSection === "VOCABULARY" ? (
                                                <BookOpen size={24} />
                                            ) : activeSection === "GRAMMAR" ? (
                                                <Edit3 size={24} />
                                            ) : activeSection === "READING" ? (
                                                <Layers size={24} />
                                            ) : (
                                                <Volume2 size={24} />
                                            )}
                                        </div>
                                        {selectedMockTest?.examType === "JFT_BASIC" && (
                                            <div className="mt-2">
                                                <p className="text-sm text-stone-500 font-bold mb-1">
                                                    Section {currentSectionIndex + 1} of {availableSections.length}
                                                </p>
                                                <h2 className="text-xl font-bold text-stone-900">
                                                    {activeSection === "VOCABULARY" ? "文字・語彙 (Script & Vocabulary)" :
                                                        activeSection === "GRAMMAR" ? "会話・表現 (Conversation & Expression)" :
                                                            activeSection === "READING" ? "読解 (Reading Comprehension)" : "聴解 (Listening Comprehension)"}
                                                </h2>
                                            </div>
                                        )}
                                    </div>

                                    {selectedMockTest?.examType === "JFT_BASIC" ? (
                                        // JFT-Basic stats
                                        <div className="bg-stone-50/60 border border-stone-200/80 rounded-xl p-5 space-y-3.5 text-xs text-stone-700 font-sans select-none mb-6">
                                            <div className="flex justify-between items-center text-stone-700">
                                                <span>セクション問題数 (Questions):</span>
                                                <span className="font-bold text-stone-900 text-sm">{activeSectionQuestions.length} 問</span>
                                            </div>
                                            <div className="flex justify-between items-center border-t border-stone-200 pt-2.5">
                                                <span>セクション配点 (Section Marks):</span>
                                                <span className="font-bold text-stone-900 text-sm">
                                                    {activeSectionQuestions.reduce((acc, q) => acc + q.marks, 0)} 点
                                                </span>
                                            </div>
                                            {currentSectionIndex === 0 && (
                                                <div className="flex justify-between items-center border-t border-stone-200 pt-2.5">
                                                    <span className="font-semibold text-stone-700">全体の制限時間 (Total Time Limit):</span>
                                                    <span className="font-bold text-base text-slate-900">
                                                        {selectedMockTest.duration} 分
                                                    </span>
                                                </div>
                                            )}
                                            <div className="flex flex-col gap-1.5 border-t border-stone-200 pt-3">
                                                <span className="text-xs font-bold text-stone-600">解答上の注意 (Guidelines):</span>
                                                <span className="text-xs text-stone-750 leading-relaxed bg-white p-3 border border-stone-150 rounded-lg">
                                                    <p className="leading-relaxed">
                                                        📖 このセクションの問題の間を自由に移動して解答ができます。他のセクションへ移動して解答を修正することも可能です。<br />
                                                        <span className="text-stone-400 text-xs mt-1 block">You can freely navigate and answer questions within any section until the exam is submitted.</span>
                                                    </p>
                                                    {activeSection === "LISTENING" && "🔊 聴解（リスニング）問題を開始する前に、音量を確認するためのテスト画面が表示されます。"}
                                                </span>
                                            </div>
                                        </div>
                                    ) : (
                                        // JLPT Stats
                                        <div className="bg-stone-50/60 border border-stone-200/80 rounded-xl p-5 space-y-3.5 text-xs text-stone-700 font-sans select-none mb-6">
                                            <div className="flex justify-between items-center text-stone-700">
                                                <span>セクション問題数 (Questions):</span>
                                                <span className="font-bold text-stone-900 text-sm">{activeSectionQuestions.length} 問</span>
                                            </div>
                                            <div className="flex justify-between items-center border-t border-stone-200 pt-2.5">
                                                <span>配点基準 (Section Marks):</span>
                                                <span className="font-bold text-stone-900 text-sm">
                                                    {activeSectionQuestions.reduce((acc, q) => acc + q.marks, 0)} 点
                                                </span>
                                            </div>
                                            <div className="flex justify-between items-center border-t border-stone-200 pt-2.5">
                                                <span className="font-semibold text-stone-700">制限時間 (Time Limit):</span>
                                                <span className={cn(
                                                    "font-bold text-base",
                                                    activeSection === "VOCABULARY" ? "text-blue-600" :
                                                        activeSection === "GRAMMAR" ? "text-amber-600" :
                                                            activeSection === "READING" ? "text-emerald-600" : "text-purple-600"
                                                )}>
                                                    {sectionTimings[activeSection as keyof SectionTimings] ?? DEFAULT_SECTION_TIMINGS[activeSection as keyof SectionTimings]} 分
                                                </span>
                                            </div>
                                            <div className="flex flex-col gap-1.5 border-t border-stone-200 pt-3">
                                                <span className="text-xs font-bold text-stone-600">解答上の注意 (Guidelines):</span>
                                                <span className="text-xs text-stone-750 leading-relaxed bg-white p-3 border border-stone-150 rounded-lg">
                                                    {activeSection === "LISTENING"
                                                        ? "🔊 聴解音声は一度しか流れません。音声再生中は解答の選択がロックされます。音量を調整して集中してください。"
                                                        : activeSection === "READING"
                                                            ? "📖 読解文は左側の用紙に表示されます。右側の用紙で問題と選択肢を確認してください。見直しマークを付けることができます。"
                                                            : "📝 問題と選択肢をよく読み、最も適当なものを選択してください。次のセクションへ進むと、このセクションの変更はできなくなります。"}
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    <div className="space-y-4 font-sans select-none">
                                        {selectedMockTest?.examType !== "JFT_BASIC" && (
                                            <div className="p-4 bg-amber-50/50 border border-amber-200/80 rounded-xl text-[11px] text-amber-850 flex items-start gap-2.5">
                                                <AlertCircle className="size-4 shrink-0 text-amber-600 mt-0.5" />
                                                <span>
                                                    <strong>戻り制限 (Progression Rule):</strong> このセクションを終了して次へ進むと、後から解答を修正することはできません。
                                                </span>
                                            </div>
                                        )}

                                        <Button
                                            className="w-full h-11 text-sm font-bold bg-slate-900 hover:bg-slate-800 text-white rounded-xl shadow-xs transition-all font-sans"
                                            onClick={() => setIsSectionGateOpen(false)}
                                        >
                                            {selectedMockTest?.examType === "JFT_BASIC" && currentSectionIndex === 0 ? "試験を開始する / Start Exam" : "解答を開始する / Start Section"}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            /* CBT Active Workspace Flow */
                            <div className="flex-1 flex overflow-hidden bg-[#f4f3f0] relative">
                                {/* Main Scrollable Question Booklet */}
                                <div
                                    className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-8 sm:space-y-12 scroll-smooth"
                                    id="questions-scroll-container"
                                >
                                    {groupedSections.map((section) => (
                                        <div key={section.category} className="space-y-6 max-w-4xl mx-auto">
                                            {/* Groups */}
                                            <div className="space-y-6 sm:space-y-8">
                                                {section.groups.map((group, groupIdx) => {
                                                    const hasScenario = group.scenario !== null;
                                                    const scenarioId = group.scenario?.id || `no-scenario-${section.category}-${groupIdx}`;

                                                    if (hasScenario) {
                                                        const scenarioAudioUrl = group.scenario.audioUrl || null;

                                                        return (
                                                            <ScenarioBlock
                                                                key={scenarioId}
                                                                scenario={group.scenario}
                                                                questionCount={group.questions.length}
                                                                audioElement={
                                                                    scenarioAudioUrl ? (
                                                                        <CustomSecureAudioPlayer
                                                                            id={scenarioId}
                                                                            attemptId={mockAttemptId}
                                                                            url={scenarioAudioUrl}
                                                                            resolveUrl={mockAttemptId ? (raw) => resolveSignedAudioUrl(raw, mockAttemptId) : undefined}
                                                                            playLimit={getPlayLimitFromUrl(scenarioAudioUrl, selectedMockTest?.examType === "JFT_BASIC" ? 2 : 0)}
                                                                            onEnded={() => markAudioAsPlayedForId(scenarioId)}
                                                                        />
                                                                    ) : undefined
                                                                }
                                                            >

                                                                <div className="space-y-6 transition-all duration-300">
                                                                    {group.questions.map((q) => {
                                                                        const qAudioUrl = q.audioUrl || null;

                                                                        return (
                                                                            <div key={q.id} className="relative space-y-3">
                                                                                <QuestionCard
                                                                                    q={q}
                                                                                    selectedMockTest={selectedMockTest}
                                                                                    selectedQuestions={selectedQuestions}
                                                                                    activeSectionQuestions={activeSectionQuestions}
                                                                                    mockAnswers={mockAnswers}
                                                                                    flaggedQuestionIds={flaggedQuestionIds}
                                                                                    highlightedQuestionId={highlightedQuestionId}
                                                                                    mockAttemptId={mockAttemptId}
                                                                                    toggleFlagQuestion={toggleFlagQuestion}
                                                                                    handleSelectAnswer={handleSelectAnswer}
                                                                                    onBypassAudio={bypassAudio}
                                                                                    hideImage={!!q.imageUrl}
                                                                                    topElement={q.imageUrl ? (
                                                                                        <div className="mb-4">
                                                                                            <img src={q.imageUrl} alt="" className="max-h-[300px] w-full max-w-xl object-contain pointer-events-none mx-auto" />
                                                                                        </div>
                                                                                    ) : undefined}
                                                                                    audioElement={qAudioUrl ? (
                                                                                        <CustomSecureAudioPlayer
                                                                                            id={q.id}
                                                                                            attemptId={mockAttemptId}
                                                                                            url={qAudioUrl}
                                                                                            resolveUrl={mockAttemptId ? (raw) => resolveSignedAudioUrl(raw, mockAttemptId) : undefined}
                                                                                            playLimit={getPlayLimitFromUrl(qAudioUrl, selectedMockTest?.examType === "JFT_BASIC" ? 2 : 0)}
                                                                                            onEnded={() => markAudioAsPlayedForId(q.id)}
                                                                                        />
                                                                                    ) : undefined}
                                                                                />
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>


                                                            </ScenarioBlock>
                                                        );
                                                    } else {
                                                        return (
                                                            <div key={scenarioId} className="space-y-6">
                                                                {group.questions.map((q) => {
                                                                    const qAudioUrl = q.audioUrl || null;

                                                                    return (
                                                                        <div key={q.id} className="relative space-y-4">
                                                                            <QuestionCard
                                                                                q={q}
                                                                                selectedMockTest={selectedMockTest}
                                                                                selectedQuestions={selectedQuestions}
                                                                                activeSectionQuestions={activeSectionQuestions}
                                                                                mockAnswers={mockAnswers}
                                                                                flaggedQuestionIds={flaggedQuestionIds}
                                                                                highlightedQuestionId={highlightedQuestionId}
                                                                                mockAttemptId={mockAttemptId}
                                                                                toggleFlagQuestion={toggleFlagQuestion}
                                                                                handleSelectAnswer={handleSelectAnswer}
                                                                                onBypassAudio={bypassAudio}
                                                                                audioElement={qAudioUrl ? (
                                                                                    <CustomSecureAudioPlayer
                                                                                        id={q.id}
                                                                                        attemptId={mockAttemptId}
                                                                                        url={qAudioUrl}
                                                                                        resolveUrl={mockAttemptId ? (raw) => resolveSignedAudioUrl(raw, mockAttemptId) : undefined}
                                                                                        playLimit={getPlayLimitFromUrl(qAudioUrl, selectedMockTest?.examType === "JFT_BASIC" ? 2 : 0)}
                                                                                        onEnded={() => markAudioAsPlayedForId(q.id)}
                                                                                    />
                                                                                ) : undefined}
                                                                            />
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        );
                                                    }
                                                })}
                                            </div>
                                        </div>
                                    ))}

                                    {/* Submission Button at the bottom of the page */}
                                    <div className="pt-8 pb-12 flex justify-center max-w-4xl mx-auto select-none">
                                        {currentExamConfig?.passCriteria !== "SECTIONAL_REQUIRED" ? (
                                            currentSectionIndex < availableSections.length - 1 ? (
                                                <Button
                                                    type="button"
                                                    variant="default"
                                                    size="lg"
                                                    className="font-bold bg-slate-900 hover:bg-slate-800 text-white rounded-xl border border-slate-950 px-8 h-12 shadow-md transition-all font-sans text-sm"
                                                    onClick={() => {
                                                        setCurrentSectionIndex(prev => prev + 1);
                                                        setActiveQuestionIndex(0);
                                                    }}
                                                >
                                                    次のセクションへ / Next Section
                                                </Button>
                                            ) : (
                                                <Button
                                                    type="button"
                                                    variant="default"
                                                    size="lg"
                                                    className="font-bold bg-slate-900 hover:bg-slate-800 text-white rounded-xl border border-slate-950 px-8 h-12 shadow-md transition-all font-sans text-sm"
                                                    onClick={() => setConfirmModalType("submit")}
                                                >
                                                    模擬試験の提出 / Submit Mock Test
                                                </Button>
                                            )
                                        ) : currentSectionIndex < availableSections.length - 1 ? (
                                            <Button
                                                type="button"
                                                variant="default"
                                                size="lg"
                                                className="font-bold bg-slate-900 hover:bg-slate-800 text-white rounded-xl border border-slate-950 px-8 h-12 shadow-md transition-all font-sans text-sm"
                                                onClick={handleNextSectionOrSubmit}
                                            >
                                                セクションを提出して次へ
                                            </Button>
                                        ) : (
                                            <Button
                                                type="button"
                                                variant="default"
                                                size="lg"
                                                className="font-bold bg-slate-900 hover:bg-slate-800 text-white rounded-xl border border-slate-950 px-8 h-12 shadow-md transition-all font-sans text-sm"
                                                onClick={handleNextSectionOrSubmit}
                                            >
                                                模擬試験の提出 / Submit Mock Test
                                            </Button>
                                        )}
                                    </div>
                                </div>

                                {/* Collapsible Right Navigator Sidebar */}
                                <div
                                    className={cn(
                                        "border-l border-stone-300 bg-white hidden lg:flex flex-col h-full shrink-0 transition-all duration-300 ease-in-out select-none font-sans relative shadow-none",
                                        isNavigatorOpen ? "w-72" : "w-0 overflow-hidden border-l-0"
                                    )}
                                >
                                    {isNavigatorOpen && (
                                        <div className="flex-1 flex flex-col overflow-hidden p-5 space-y-5">
                                            <div className="flex items-center justify-between border-b border-stone-200 pb-3">
                                                <div className="flex items-center gap-1.5">
                                                    <Layers size={14} className="text-stone-700" />
                                                    <span className="font-bold text-xs uppercase tracking-wider text-stone-850">試験ナビゲーター</span>
                                                </div>
                                                <span className="text-[10px] bg-stone-200 text-stone-700 px-2 py-0.5 rounded font-mono font-bold">
                                                    {currentExamConfig?.passCriteria !== "SECTIONAL_REQUIRED"
                                                        ? `${answeredCountGlobal}/${totalMockQuestions} 解答`
                                                        : `${answeredCountSection}/${activeSectionQuestions.length} 解答`}
                                                </span>
                                            </div>


                                            {/* Question Grid */}
                                            {currentExamConfig?.passCriteria !== "SECTIONAL_REQUIRED" ? (
                                                <div className="flex-1 overflow-y-auto pr-1 space-y-4">
                                                    {(() => {
                                                        const categories = Object.keys(sectionBreakdown);
                                                        return categories.map((cat) => {
                                                            const catQs = selectedQuestions.filter(q => getQuestionCategory(q, currentExamConfig) === cat);
                                                            if (catQs.length === 0) return null;
                                                            const catLabel = getSectionDisplay(cat, true);
                                                            return (
                                                                <div key={cat} className="space-y-2">
                                                                    <div className="text-[10px] font-bold text-stone-500 uppercase tracking-wider border-b border-stone-100 pb-1">
                                                                        {catLabel}
                                                                    </div>
                                                                    <div className="grid grid-cols-4 gap-2 p-0.5">
                                                                        {catQs.map((q, idxInSection) => {
                                                                            const globalIdx = selectedQuestions.findIndex(sq => sq.id === q.id);
                                                                            const isAnswered = isQuestionAnswered(q, mockAnswers[q.id]);
                                                                            const isFlagged = flaggedQuestionIds.includes(q.id);
                                                                            const isCurrentSection = cat === activeSection;
                                                                            const isActive = isCurrentSection && activeQuestionIndex === idxInSection;

                                                                            return (
                                                                                <button
                                                                                    key={q.id}
                                                                                    type="button"
                                                                                    onClick={() => {
                                                                                        const secIdx = availableSections.indexOf(cat);
                                                                                        if (secIdx !== -1 && currentSectionIndex !== secIdx) {
                                                                                            toast.warning("セクション間の移動はできません。 / Cannot jump between sections.");
                                                                                            return;
                                                                                        }
                                                                                        setActiveQuestionIndex(idxInSection);
                                                                                        setTimeout(() => {
                                                                                            const element = document.getElementById(`question-card-${q.id}`);
                                                                                            if (element) {
                                                                                                element.scrollIntoView({ behavior: "smooth", block: "center" });
                                                                                                setHighlightedQuestionId(q.id);
                                                                                                setTimeout(() => setHighlightedQuestionId(null), 1550);
                                                                                            }
                                                                                        }, 50);
                                                                                    }}
                                                                                    className={cn(
                                                                                        "h-10 rounded border text-xs font-mono font-bold flex flex-col items-center justify-center relative transition-all",
                                                                                        isAnswered
                                                                                            ? "bg-stone-900 border-stone-950 text-white"
                                                                                            : "bg-white border-stone-200 text-stone-550 hover:bg-stone-50",
                                                                                        isActive
                                                                                            ? "ring-2 ring-stone-900 ring-offset-2 border-stone-950"
                                                                                            : ""
                                                                                    )}
                                                                                >
                                                                                    <span>{globalIdx + 1}</span>

                                                                                </button>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            );
                                                        });
                                                    })()}
                                                </div>
                                            ) : (
                                                /* Active Section Question Grid */
                                                <div className="flex-1 overflow-y-auto pr-1">
                                                    <div className="grid grid-cols-4 gap-2 p-0.5">
                                                        {activeSectionQuestions.map((q, idx) => {
                                                            const isAnswered = isQuestionAnswered(q, mockAnswers[q.id]);
                                                            const isFlagged = flaggedQuestionIds.includes(q.id);
                                                            const isActive = idx === activeQuestionIndex;

                                                            return (
                                                                <button
                                                                    key={q.id}
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setActiveQuestionIndex(idx);
                                                                        const element = document.getElementById(`question-card-${q.id}`);
                                                                        if (element) {
                                                                            element.scrollIntoView({ behavior: "smooth", block: "center" });
                                                                            setHighlightedQuestionId(q.id);
                                                                            setTimeout(() => setHighlightedQuestionId(null), 1550);
                                                                        }
                                                                    }}
                                                                    className={cn(
                                                                        "h-10 rounded border text-xs font-mono font-bold flex flex-col items-center justify-center relative transition-all",
                                                                        isAnswered
                                                                            ? "bg-stone-900 border-stone-950 text-white"
                                                                            : "bg-white border-stone-200 text-stone-550 hover:bg-stone-50",
                                                                        isActive
                                                                            ? "ring-2 ring-stone-900 ring-offset-2 border-stone-950"
                                                                            : ""
                                                                    )}
                                                                >
                                                                    <span>{idx + 1}</span>

                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Section Progression Summary */}
                                            <div className="border-t border-stone-200 pt-4 space-y-3 shrink-0">
                                                <div className="flex justify-between items-center text-xs text-stone-600 font-medium">
                                                    <span>模試全体の進捗</span>
                                                    <span className="font-bold text-stone-900 font-mono">
                                                        {answeredCountGlobal} / {totalMockQuestions}
                                                    </span>
                                                </div>
                                                <Progress value={totalMockQuestions > 0 ? (answeredCountGlobal / totalMockQuestions) * 100 : 0} className="h-1.5 bg-stone-200" />
                                            </div>
                                        </div>
                                    )}
                                </div>

                            </div>
                        )}

                    </div>

                    {/* Confirmation Stepper Modal Overlay */}
                    {confirmModalType && (
                        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/55 backdrop-blur-xs p-4 animate-in fade-in duration-200 select-none font-sans">
                            <div className="w-full max-w-md bg-white border border-stone-300 p-6 shadow-xl rounded-2xl space-y-6 relative">
                                <button
                                    onClick={() => setConfirmModalType(null)}
                                    className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-stone-100 text-stone-500 hover:text-stone-700 transition-colors"
                                >
                                    <X size={18} strokeWidth={2.5} />
                                </button>

                                <div className="space-y-2">
                                    <h3 className="text-lg font-bold text-stone-900 pr-8">
                                        {confirmModalType === "section" ? "セクションを終了して進みますか？" : confirmModalType === "exit" ? "一時退室しますか？" : "模擬試験を提出しますか？"}
                                    </h3>
                                    <p className="text-sm text-stone-500 leading-relaxed">
                                        {confirmModalType === "section"
                                            ? "一度このセクションを終了すると、解答を修正するために戻ることはできません。未解答の問題は0点になります。"
                                            : confirmModalType === "exit"
                                                ? "退室しても解答は保存されます。後で再開することができます。"
                                                : "模擬試験を終了して解答を提出します。提出後は解答の変更はできません。本当によろしいですか？"}
                                    </p>
                                </div>

                                <div className="flex justify-end gap-3 pt-2">
                                    {confirmModalType === "exit" ? (
                                        <>
                                            <Button
                                                type="button"
                                                variant="default"
                                                size="sm"
                                                onClick={() => {
                                                    setConfirmModalType(null);
                                                    void confirmExitCbt();
                                                }}
                                                className="font-bold rounded-xl"
                                            >
                                                Save & Resume Later
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="destructive"
                                                size="sm"
                                                onClick={() => {
                                                    setConfirmModalType(null);
                                                    void handleSubmitMockTest();
                                                }}
                                                className="font-bold rounded-xl"
                                            >
                                                Submit Exam Now
                                            </Button>
                                        </>
                                    ) : (
                                        <Button
                                            type="button"
                                            variant={confirmModalType === "submit" ? "destructive" : "default"}
                                            size="sm"
                                            onClick={() => {
                                                const type = confirmModalType;
                                                setConfirmModalType(null);
                                                if (type === "section") {
                                                    void confirmProceedSection();
                                                } else {
                                                    void handleSubmitMockTest();
                                                }
                                            }}
                                            className="font-bold rounded-xl"
                                        >
                                            {confirmModalType === "submit" ? "Submit Mock Test" : "Proceed"}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            </ExamSecurityWrapper>
        );
    }
    // Define filter controls
    const filterControls = (
        <div className="flex flex-col gap-4 bg-muted/20 border border-border/60 p-4 rounded-2xl font-sans text-xs">
            <div className="flex flex-wrap items-center justify-between gap-4">
                {/* Exam Type Filter */}
                <div className="flex items-center gap-2">
                    <span className="font-semibold text-muted-foreground">Type:</span>
                    <select
                        value={selectedExamType}
                        onChange={(e) => {
                            setSelectedExamType(e.target.value);
                            setSelectedLevel("ALL"); // Reset level when type changes
                        }}
                        className="h-8 px-3 rounded-lg border border-border bg-background text-xs text-foreground focus:ring-1 focus:ring-primary outline-none cursor-pointer"
                    >
                        <option value="ALL">All Types</option>
                        {activeConfigs.map(c => (
                            <option key={c.code} value={c.code}>{c.name}</option>
                        ))}
                    </select>
                </div>

                {/* Sort selector */}
                <div className="flex items-center gap-2">
                    <span className="font-semibold text-muted-foreground">Sort:</span>
                    <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value as any)}
                        className="h-8 px-3 rounded-lg border border-border bg-background text-xs text-foreground focus:ring-1 focus:ring-primary outline-none cursor-pointer"
                    >
                        <option value="newest">Newest</option>
                        <option value="popular">Popular</option>
                        <option value="price">Price</option>
                    </select>
                </div>
            </div>

            {/* Level pills */}
            <div className="border-t border-border/40 pt-3">
                <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-none">
                    {levels.map((lvl) => {
                        const isSelected = lvl === selectedLevel;
                        return (
                            <button
                                key={lvl}
                                type="button"
                                onClick={() => setSelectedLevel(lvl)}
                                className={cn(
                                    "px-3 py-1.5 rounded-full text-xs font-semibold tracking-wide transition-all duration-200 shrink-0",
                                    isSelected
                                        ? "bg-primary text-primary-foreground shadow-sm"
                                        : "bg-background border border-border text-muted-foreground hover:text-foreground",
                                )}
                            >
                                {lvl === "ALL" ? "All Levels" : lvl}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );

    // Dashboard / Standard Mock Test List View
    return (
        <>
            <DashboardLayout showSidebar={!!user}>
                <div className="dash-page">
                    <div className="dash-page-header">
                        <div>
                            <h1 className="dash-page-title">Mock tests</h1>
                            <p className="dash-page-subtitle">
                                Choose a level, pick a test, then practice or preview. Admins create new tests first, then build questions on the
                                editor page. Teachers open the editor only for tests assigned to them.
                            </p>
                        </div>
                        {isAdmin ? (
                            <Button asChild className="shrink-0">
                                <Link to="/admin/mock-tests/new">
                                    <PlusCircle className="size-4 mr-2 inline" aria-hidden />
                                    New mock test
                                </Link>
                            </Button>
                        ) : null}
                    </div>

                    {loading ? (
                        <div className="dash-empty-state flex flex-row items-center justify-center gap-2">
                            <Loader2 className="animate-spin size-4" /> Loading mock tests…
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {filterControls}
                            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
                                {/* Left column height is driven by the right panel: its content is
                                    absolutely positioned on lg so it never grows the row, letting the
                                    list fill the right panel's height and scroll only when taller. */}
                                <div className="lg:col-span-5 lg:relative min-h-0">
                                    <div className="flex flex-col gap-3 lg:absolute lg:inset-0">
                                        <h2 className="text-sm font-semibold text-foreground">Tests Catalogue ({filteredMockTests.length})</h2>
                                        {testPicker}
                                    </div>
                                </div>
                                <div className="lg:col-span-7">{takeTestPanel}</div>
                            </div>
                        </div>
                    )}
                </div>
            </DashboardLayout>
            {showAnswersReview && (
                <div className="fixed inset-0 z-[100] bg-[#f4f3f0] overflow-y-auto animate-in fade-in duration-200">
                    {reviewQuery.isLoading ? (
                        <div className="flex h-full items-center justify-center">
                            <div className="animate-spin w-8 h-8 border-4 border-stone-800 border-t-transparent rounded-full" />
                        </div>
                    ) : reviewQuery.data ? (
                        <ExamReviewPanel
                            questions={reviewQuery.data as ReviewableQuestion[]}
                            answers={(mockResult?.answers as Record<string, unknown>) || mockAnswers}
                            attemptId={mockResult?.id}
                            title="模擬試験 解答解説 / Mock Test Answer Review"
                            onClose={() => setShowAnswersReview(false)}
                            examType={selectedMockTest?.examType}
                        />
                    ) : (
                        <div className="flex h-full flex-col items-center justify-center p-4">
                            <p className="text-red-600 mb-4">Could not load review data. {reviewQuery.error ? String(reviewQuery.error) : ""}</p>
                            <button onClick={() => setShowAnswersReview(false)} className="px-4 py-2 bg-stone-200 rounded">Close</button>
                        </div>
                    )}
                </div>
            )}
        </>
    );

    function handleSelectAnswer(qId: string, origIndex: number) {
        const next = { ...mockAnswers, [qId]: origIndex };
        setMockAnswers(next);
    }
}

// =========================================================================
// HELPER COMPONENTS FOR THE REDESIGNED EXAM EXPERIENCE
// =========================================================================

interface QuestionCardProps {
    q: MockTestQuestion;
    selectedMockTest: MockTest | null;
    selectedQuestions: MockTestQuestion[];
    activeSectionQuestions: MockTestQuestion[];
    mockAnswers: Record<string, unknown>;
    flaggedQuestionIds: string[];
    highlightedQuestionId: string | null;
    mockAttemptId: string | null;
    toggleFlagQuestion: (qId: string) => void;
    handleSelectAnswer: (qId: string, origIndex: number) => void;
    hideImage?: boolean;
    topElement?: React.ReactNode;
    audioElement?: React.ReactNode;
    onBypassAudio?: () => void;
}

const QuestionCard = ({
    q,
    selectedMockTest,
    selectedQuestions,
    activeSectionQuestions,
    mockAnswers,
    flaggedQuestionIds,
    highlightedQuestionId,
    mockAttemptId,
    toggleFlagQuestion,
    handleSelectAnswer,
    hideImage,
    topElement,
    audioElement,
    onBypassAudio
}: QuestionCardProps) => {
    const globalIdx = selectedQuestions.findIndex((sq) => sq.id === q.id);
    const sectionIdx = activeSectionQuestions.findIndex((sq) => sq.id === q.id);
    const displayIdx = selectedMockTest?.examType === "JFT_BASIC" ? globalIdx + 1 : sectionIdx + 1;

    const isSelected = (origIndex: number) => mockAnswers[q.id] === origIndex;
    const isFlagged = flaggedQuestionIds.includes(q.id);
    const isHighlighted = highlightedQuestionId === q.id;

    const qOptPairs = buildMcqDisplayPairs(q, true, `${mockAttemptId}:${q.id}:mcqopts`);
    const optionType = (q.options as { metadata?: any } | null | undefined)?.metadata?.optionType || "TEXT";

    return (
        <div
            id={`question-card-${q.id}`}
            className={cn(
                "bg-white border-y border-x md:border p-6 md:p-8 shadow-xs flex flex-col rounded-xl transition-all duration-300 scroll-mt-20 relative border-stone-200 hover:border-stone-350",
                isHighlighted
                    ? "border-amber-400 border-l-amber-500 bg-amber-50/10 ring-2 ring-amber-400 shadow-md scale-[1.01]"
                    : ""
            )}

        >
            {/* Header info */}
            <div className="flex items-center justify-between text-xs text-stone-500 border-b border-stone-200 pb-2 mb-4 font-sans select-none">
                <div className="flex items-center gap-2">
                    <span className="font-bold text-stone-850 text-sm font-sans">問 {displayIdx}</span>
                    <span>({q.marks} 点)</span>
                </div>
            </div>

            <div className="space-y-6">
                {topElement && <div>{topElement}</div>}

                {audioElement && <div className="mb-4 relative z-20">{audioElement}</div>}

                <div className="transition-all duration-300">
                    <NoCopyQuestionStem as="h2" className="text-[1.08rem] font-medium leading-relaxed text-stone-900 mb-3" >
                        {renderFurigana(q.questionText)}
                    </NoCopyQuestionStem>

                    {!hideImage && q.imageUrl && (
                        <img src={q.imageUrl} alt="" className="max-h-[480px] w-full max-w-2xl object-contain my-3 pointer-events-none mx-auto" />
                    )}

                    {q.type === "MCQ" || q.type === "LISTENING" ? (
                        (() => {
                            if (optionType === "IMAGE") {
                                return (
                                    <div className="grid grid-cols-2 gap-3 mt-4 select-none">
                                        {qOptPairs.map(({ text, origIndex, imageUrl, audioUrl }, idx) => {
                                            const selected = isSelected(origIndex);
                                            return (
                                                <button
                                                    key={`${q.id}-${origIndex}`}
                                                    type="button"
                                                    onClick={() => handleSelectAnswer(q.id, origIndex)}
                                                    className={cn(
                                                        "relative flex flex-col items-center justify-center rounded-xl border p-3 aspect-square transition-all duration-200 shadow-xs hover:-translate-y-0.5 hover:shadow-md",
                                                        selected
                                                            ? "border-primary bg-primary/10 text-primary font-semibold shadow-sm -translate-y-0.5 ring-1 ring-primary"
                                                            : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                                                    )}
                                                >
                                                    <span className={cn(
                                                        "absolute top-3 left-3 text-sm font-bold select-none leading-none",
                                                        selected ? "text-slate-900" : "text-slate-400"
                                                    )}>
                                                        {getCircleBadge(idx)}
                                                    </span>
                                                    {imageUrl ? (
                                                        <img src={imageUrl} alt="" className="max-h-[85%] max-w-[85%] object-contain pointer-events-none" />
                                                    ) : (
                                                        <span className="text-[10px] text-muted-foreground italic">(No Image)</span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                );
                            }

                            if (optionType === "MIXED") {
                                return (
                                    <div className="grid grid-cols-1 gap-3 mt-4 select-none">
                                        {qOptPairs.map(({ text, origIndex, imageUrl, audioUrl }, idx) => {
                                            const selected = isSelected(origIndex);
                                            return (
                                                <button
                                                    key={`${q.id}-${origIndex}`}
                                                    type="button"
                                                    onClick={() => handleSelectAnswer(q.id, origIndex)}
                                                    className={cn(
                                                        "w-full text-left rounded-xl border p-4 flex gap-3.5 transition-all duration-200 text-base shadow-xs hover:-translate-y-0.5 hover:shadow-md",
                                                        selected
                                                            ? "border-primary bg-primary/10 text-primary font-semibold shadow-sm -translate-y-0.5 ring-1 ring-primary"
                                                            : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                                                    )}
                                                >
                                                    <span className={cn(
                                                        "text-lg shrink-0 select-none leading-none pt-0.5 font-bold transition-colors",
                                                        selected ? "text-primary" : "text-slate-400"
                                                    )}>
                                                        {getCircleBadge(idx)}
                                                    </span>
                                                    <div className="flex-1 min-w-0 flex items-center justify-between gap-3">
                                                        <span className="text-[15px] block leading-snug">
                                                            {renderFurigana(text || `Option ${getCircleBadge(idx)}`)}
                                                        </span>
                                                        {imageUrl && (
                                                            <div className="w-16 h-12 shrink-0 overflow-hidden flex items-center justify-center p-0.5">
                                                                <img src={imageUrl} alt="" className="max-h-full max-w-full object-contain pointer-events-none" />
                                                            </div>
                                                        )}
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                );
                            }

                            // Default TEXT/Fallback layout
                            return (
                                <div className={cn(
                                    "grid gap-3 select-none",
                                    isHorizontalLayout(qOptPairs) ? "grid-cols-2 md:grid-cols-4" : "grid-cols-1 sm:grid-cols-2"
                                )}>
                                    {qOptPairs.map(({ text, origIndex }, idx) => {
                                        const selected = isSelected(origIndex);
                                        return (
                                            <button
                                                key={`${q.id}-${origIndex}`}
                                                type="button"
                                                onClick={() => handleSelectAnswer(q.id, origIndex)}
                                                className={cn(
                                                    "w-full text-left rounded-xl border p-4 flex gap-3.5 transition-all duration-200 text-base shadow-xs hover:-translate-y-0.5 hover:shadow-md",
                                                    selected
                                                        ? "border-primary bg-primary/10 text-primary font-semibold shadow-sm -translate-y-0.5 ring-1 ring-primary"
                                                        : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                                                )}

                                            >
                                                <span className={cn(
                                                    "text-lg shrink-0 select-none leading-none pt-0.5 font-bold transition-colors",
                                                    selected ? "text-slate-900" : "text-slate-400"
                                                )}>
                                                    {getCircleBadge(idx)}
                                                </span>
                                                <div className="min-w-0 flex-1 space-y-2">
                                                    {text && text.trim().length > 0 && (
                                                        <div className="leading-relaxed text-[15px]">
                                                            {renderFurigana(text)}
                                                        </div>
                                                    )}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            );
                        })()
                    ) : (
                        <p className="text-xs text-stone-400 font-sans">Unsupported question type in Mock CBT.</p>
                    )}
                </div>
            </div>
        </div>
    );
};
